/*
 * ARTMiE — loyalty points, inside the cart drawer.
 *
 * WHAT THIS IS. One card, rendered by the theme itself (snippets/amh-loyalty-cart.liquid, called
 * from snippets/cart-drawer.liquid next to the discount box) and filled in by this script. A second,
 * simpler renderer draws the "My points" page. There is no floating badge and nothing on /cart —
 * that page has no drawer, and the owner asked for the sidebar only.
 *
 * THE FIVE RULES THIS FILE EXISTS TO OBEY, each learned the hard way on artmie.ba:
 *
 * 1. NEVER NAVIGATE. Horizon redraws the cart by posting to the cart API and morphing the returned
 *    section HTML in place. The previous version used /discount/CODE?redirect=/cart, which is a full
 *    page load: it tore the drawer down, dumped the shopper on /cart, and killed the celebration.
 *    Every action here is a fetch plus a theme event.
 *
 * 2. ASSUME THE CARD IS WIPED AFTER EVERY CART CHANGE. morph() diffs the whole header section. Our
 *    node is a custom tag so it can never be mutated INTO cart markup, but its children come from
 *    the server (empty) on every redraw. mount() is therefore cheap, idempotent, and re-runs on the
 *    theme's own cart:update / cart:refresh / discount:update events. No MutationObserver: there are
 *    already three under the drawer and our own writes would feed them.
 *
 * 3. NO NATIVE <select>. The popup is drawn by the OS and cannot be themed. This is an inline
 *    expanding listbox — inline, because .cart-drawer__content scrolls and .cart-drawer__inner and
 *    the dialog both clip, so anything absolutely positioned gets cut off.
 *
 * 4. THE FREE PRODUCT IS A BUY-X-GET-Y CODE. Shopify never adds the free item and never removes it;
 *    it only zeroes a line that is already in the basket, and it REFUSES the code when the item is
 *    missing. So: add the variant, then apply the code. And when such a voucher dies, the line is
 *    still there at full price — we say so and offer to remove it, we never delete it silently.
 *
 * 5. NEVER WIPE SOMEBODY ELSE'S DISCOUNT. {discount:""} means "remove every code on this basket".
 *    Always re-read the live list immediately before a write, and abort when the read failed rather
 *    than treating a failure as an empty basket.
 *
 * Identity is the signed token the snippet mints from customer.id + email + today, verified by the
 * Hub. Nothing here trusts anything the browser could type. Never throws into the storefront.
 */
(function () {
  "use strict";
  /**
   * BUILD — raise it with every change to this file (YYYYMMDD + two digits).
   *
   * The card reaches a shop two ways: the Hub's Reinstall button, which sends the Hub server's copy,
   * and scripts/loyalty-asset-push.ts, which sends the working copy. A checksum can say the two
   * differ but not which is newer, and on 1.10.2026 Reinstall twice put an older card back over a
   * newer one. This number is how each side tells: neither will replace a higher build with a lower.
   */
  var BUILD = 2026102202;
  try { window.__amhLoyaltyBuild = BUILD; } catch (e) { /* never throw into the storefront */ }
  if (window.__amhLoyalty) return;
  window.__amhLoyalty = true;

  // ─────────────────────────────── words ───────────────────────────────
  var STR = {
    en: { goneAuto: "You removed {t}, so we've given you back {n} points.", removeBack: "Remove (+{n} points)", addMore: "add {v} more", apply: "Apply", applied: "applied", willEarn: "You will collect about {n} points with this order", haveIt: "You already have this reward.", tooMany: "You already hold {n} rewards. Use one first.", valid: "use within {d} days", points: "points", title: "Your rewards", bal: "{n} points", 
      toGo: "{n} points to go", minSpend: "min. order {v}", until: "until {d}", applying: "Applying…",
      working: "One moment…", 
      gaveBack: "{n} points are back.",
      fail: "That did not work: {e}", none: "No rewards yet — keep collecting points.", 
      deadGone: "{c} is no longer valid, so we took it off your basket.", orphanTitle: "{t} is no longer free.", orphanBody: "It stayed in your basket and now costs {v}.",
      orphanRemove: "Remove from basket", orphanKeep: "Keep and pay", notNow: "Not ready for a moment — please try again.", unlock: "Add {v} more to unlock", loading: "Loading your rewards…", actTitle: "Recent activity", actOrder: "Order {o}", actReward: "Reward: {t}", actAdd: "Points added", actRemove: "Points removed", actClaw: "Order cancelled", actExpire: "Points expired", heroLabel: "Your points", nudgeNear: "Only {n} more points to {t}", nudgeReady: "You have enough points for a reward — choose one below.", tabRewards: "Rewards", tabActivity: "Activity", tabHow: "How it works", how1: "Earn {r} points for every {v} you spend.", how2: "Points are added after your order. If an order is cancelled or a product is removed from it, those points are taken back.", how3: "Swap points for rewards here or in your basket. Changed your mind? Remove the reward and your points come back.", howExpiry: "Your points stay valid as long as you shop at least once every {m} months.", shopNow: "Start shopping", actNone: "No activity yet — your first order starts it.", free: "FREE", save: "You save {v}", toBasket: "Go to basket", pendTotal: "+{n} points on the way", pendOrder: "Order {o} · on the way", maxPc: "max. {n} per order", pendFrom: "usable from {d}", howLock: "Points from an order can be used once it is paid or delivered, and at the earliest {n} days after the order. Until then they show as “on the way”.", actReturn: "Goods returned", pendHeld: "on hold while we handle your return", howReturn: "If you return goods or get money back, the points for it are taken back. While a return is being handled, that order’s points are on hold.", pendUnpaid: "waiting for payment", pendUndelivered: "waiting for delivery", actChanged: "Order changed", actRefund: "Money refunded", nlTitle: "Points are for newsletter subscribers", nlText: "Subscribe to our newsletter and your orders will collect points.", nlBtn: "Subscribe to the newsletter", nlDone: "Thank you, your newsletter subscription is active. Your orders now collect points.", howNl: "Points are for newsletter subscribers: your orders collect points while you are subscribed to our newsletter.", myPoints: "My points", },
    sk: { goneAuto: "Odstránili ste {t}, tak sme vám vrátili {n} bodov.", removeBack: "Odstrániť (+{n} bodov)", addMore: "pridajte ešte {v}", apply: "Použiť", applied: "použité", willEarn: "Za túto objednávku získate približne {n} bodov", haveIt: "Túto odmenu už máte.", tooMany: "Už máte {n} nevyužité odmeny. Najprv jednu použite.", valid: "použite do {d} dní", points: "bodov", title: "Vaše odmeny", bal: "{n} bodov", 
      toGo: "chýba {n} bodov", minSpend: "pri nákupe od {v}", until: "do {d}", applying: "Vkladám…",
      working: "Moment…", 
      gaveBack: "Vrátených {n} bodov.",
      fail: "Nepodarilo sa: {e}", none: "Zatiaľ žiadne odmeny — zbierajte body ďalej.", 
      deadGone: "{c} už neplatí, odstránili sme ho z košíka.", orphanTitle: "{t} už nie je zadarmo.", orphanBody: "Produkt zostal v košíku a stojí {v}.",
      orphanRemove: "Odstrániť z košíka", orphanKeep: "Nechať a zaplatiť", notNow: "Chvíľu nedostupné — skúste to prosím znova.", unlock: "Pridajte ešte {v} a odomknete", loading: "Načítavame vaše odmeny…", actTitle: "Posledná aktivita", actOrder: "Objednávka {o}", actReward: "Odmena: {t}", actAdd: "Body pripísané", actRemove: "Body odpísané", actClaw: "Objednávka zrušená", actExpire: "Body prepadli", heroLabel: "Vaše body", nudgeNear: "Už len {n} bodov do odmeny {t}", nudgeReady: "Máte dosť bodov na odmenu — vyberte si nižšie.", tabRewards: "Odmeny", tabActivity: "Aktivita", tabHow: "Ako to funguje", how1: "Za každých {v} nákupu získate {r} bodov.", how2: "Body pripíšeme po objednávke. Ak sa objednávka zruší alebo sa z nej odstráni produkt, body zaň odpočítame.", how3: "Body vymeníte za odmeny tu alebo v košíku. Rozmysleli ste si to? Odmenu odstráňte a body sa vám vrátia.", howExpiry: "Body vám zostanú, ak nakúpite aspoň raz za {m} mesiacov.", shopNow: "Začať nakupovať", actNone: "Zatiaľ žiadna aktivita — začne prvou objednávkou.", free: "ZADARMO", save: "Ušetríte {v}", toBasket: "Prejsť do košíka", pendTotal: "+{n} bodov na ceste", pendOrder: "Objednávka {o} · na ceste", maxPc: "max. {n} ks na objednávku", pendFrom: "použiteľné od {d}", howLock: "Body za objednávku môžete použiť, keď je zaplatená alebo doručená, a najskôr {n} dní po objednaní. Dovtedy ich uvidíte ako „na ceste“.", actReturn: "Vrátený tovar", pendHeld: "pozastavené, kým vybavíme vrátenie", howReturn: "Ak tovar vrátite alebo dostanete peniaze späť, body zaň odpočítame. Kým vrátenie vybavujeme, body z tejto objednávky sú pozastavené.", pendUnpaid: "čaká sa na platbu", pendUndelivered: "čaká sa na doručenie", actChanged: "Objednávka upravená", actRefund: "Vrátené peniaze", nlTitle: "Body získavajú odberatelia newslettera", nlText: "Prihláste sa na odber nášho newslettera a vaše objednávky budú zbierať body.", nlBtn: "Prihlásiť sa na odber newslettera", nlDone: "Ďakujeme, odber newslettera je aktívny. Vaše objednávky teraz zbierajú body.", howNl: "Body sú pre odberateľov newslettera: vaše objednávky zbierajú body, kým odoberáte náš newsletter.", myPoints: "Moje body", },
    cs: { goneAuto: "Odebrali jste {t}, tak jsme vám vrátili {n} bodů.", removeBack: "Odebrat (+{n} bodů)", addMore: "přidejte ještě {v}", apply: "Použít", applied: "použito", willEarn: "Za tuto objednávku získáte přibližně {n} bodů", haveIt: "Tuto odměnu už máte.", tooMany: "Už máte {n} nevyužité odměny. Nejprve jednu použijte.", valid: "využijte do {d} dnů", points: "bodů", title: "Vaše odměny", bal: "{n} bodů", 
      toGo: "chybí {n} bodů", minSpend: "při nákupu od {v}", until: "do {d}", applying: "Vkládám…",
      working: "Moment…", 
      gaveBack: "Vráceno {n} bodů.",
      fail: "Nepodařilo se: {e}", none: "Zatím žádné odměny — sbírejte body dál.", 
      deadGone: "{c} už neplatí, odebrali jsme ho z košíku.", orphanTitle: "{t} už není zdarma.", orphanBody: "Produkt zůstal v košíku a stojí {v}.",
      orphanRemove: "Odebrat z košíku", orphanKeep: "Nechat a zaplatit", notNow: "Chvíli nedostupné — zkuste to prosím znovu.", unlock: "Přidejte ještě {v} a odemknete", loading: "Načítáme vaše odměny…", actTitle: "Poslední aktivita", actOrder: "Objednávka {o}", actReward: "Odměna: {t}", actAdd: "Body připsány", actRemove: "Body odepsány", actClaw: "Objednávka zrušena", actExpire: "Body propadly", heroLabel: "Vaše body", nudgeNear: "Už jen {n} bodů do odměny {t}", nudgeReady: "Máte dost bodů na odměnu — vyberte si níže.", tabRewards: "Odměny", tabActivity: "Aktivita", tabHow: "Jak to funguje", how1: "Za každých {v} nákupu získáte {r} bodů.", how2: "Body připíšeme po objednávce. Pokud se objednávka zruší nebo se z ní odebere produkt, body za něj odečteme.", how3: "Body vyměníte za odměny zde nebo v košíku. Rozmysleli jste si to? Odměnu odeberte a body se vám vrátí.", howExpiry: "Body vám zůstanou, pokud nakoupíte alespoň jednou za {m} měsíců.", shopNow: "Začít nakupovat", actNone: "Zatím žádná aktivita — začne první objednávkou.", free: "ZDARMA", save: "Ušetříte {v}", toBasket: "Přejít do košíku", pendTotal: "+{n} bodů na cestě", pendOrder: "Objednávka {o} · na cestě", maxPc: "max. {n} ks na objednávku", pendFrom: "použitelné od {d}", howLock: "Body za objednávku můžete použít, až je zaplacená nebo doručená, a nejdříve {n} dní po objednání. Do té doby je uvidíte jako „na cestě“.", actReturn: "Vrácené zboží", pendHeld: "pozastaveno, dokud vyřídíme vrácení", howReturn: "Pokud zboží vrátíte nebo dostanete peníze zpět, body za ně odečteme. Dokud vrácení vyřizujeme, body z této objednávky jsou pozastavené.", pendUnpaid: "čeká se na platbu", pendUndelivered: "čeká se na doručení", actChanged: "Objednávka upravena", actRefund: "Vrácené peníze", nlTitle: "Body získávají odběratelé newsletteru", nlText: "Přihlaste se k odběru našeho newsletteru a vaše objednávky budou sbírat body.", nlBtn: "Přihlásit se k odběru newsletteru", nlDone: "Děkujeme, odběr newsletteru je aktivní. Vaše objednávky teď sbírají body.", howNl: "Body jsou pro odběratele newsletteru: vaše objednávky sbírají body, dokud odebíráte náš newsletter.", myPoints: "Moje body", },
    bs: { goneAuto: "Uklonili ste {t}, pa smo vam vratili {n} bodova.", removeBack: "Ukloni (+{n} bodova)", addMore: "dodajte još {v}", apply: "Primijeni", applied: "primijenjeno", willEarn: "Za ovu narudžbu dobit ćete oko {n} bodova", haveIt: "Ovu nagradu već imate.", tooMany: "Već imate {n} neiskorištene nagrade. Prvo jednu iskoristite.", valid: "iskoristite u roku od {d} dana", points: "bodova", title: "Vaše nagrade", bal: "{n} bodova", 
      toGo: "još {n} bodova", minSpend: "za narudžbe od {v}", until: "do {d}", applying: "Dodajem…",
      working: "Trenutak…", 
      gaveBack: "Vraćeno {n} bodova.",
      fail: "Nije uspjelo: {e}", none: "Još nema nagrada — nastavite skupljati bodove.", 
      deadGone: "{c} više ne važi, uklonili smo ga iz korpe.", orphanTitle: "{t} više nije besplatan.", orphanBody: "Proizvod je ostao u korpi i sada košta {v}.",
      orphanRemove: "Ukloni iz korpe", orphanKeep: "Zadrži i plati", notNow: "Trenutno nije spremno — pokušajte ponovo.", unlock: "Dodajte još {v} za otključavanje", loading: "Učitavamo vaše nagrade…", actTitle: "Nedavna aktivnost", actOrder: "Narudžba {o}", actReward: "Nagrada: {t}", actAdd: "Bodovi dodani", actRemove: "Bodovi oduzeti", actClaw: "Narudžba otkazana", actExpire: "Bodovi istekli", heroLabel: "Vaši bodovi", nudgeNear: "Još samo {n} bodova do nagrade {t}", nudgeReady: "Imate dovoljno bodova za nagradu — odaberite je ispod.", tabRewards: "Nagrade", tabActivity: "Aktivnost", tabHow: "Kako funkcioniše", how1: "Za svakih {v} kupovine dobijate {r} bodova.", how2: "Bodove dodajemo nakon narudžbe. Ako se narudžba otkaže ili se iz nje ukloni proizvod, bodovi za to se oduzimaju.", how3: "Bodove mijenjate za nagrade ovdje ili u korpi. Predomislili ste se? Uklonite nagradu i bodovi se vraćaju.", howExpiry: "Bodovi ostaju vaši dok kupujete barem jednom u {m} mjeseci.", shopNow: "Započnite kupovinu", actNone: "Još nema aktivnosti — počinje s prvom narudžbom.", free: "BESPLATNO", save: "Štedite {v}", toBasket: "Idi u korpu", pendTotal: "+{n} bodova na putu", pendOrder: "Narudžba {o} · na putu", maxPc: "max. {n} kom. po narudžbi", pendFrom: "možete koristiti od {d}", howLock: "Bodove za narudžbu možete koristiti kada je plaćena ili isporučena, a najranije {n} dana nakon narudžbe. Do tada ih vidite kao „na putu“.", actReturn: "Vraćena roba", pendHeld: "na čekanju dok rješavamo povrat", howReturn: "Ako vratite robu ili dobijete novac nazad, bodovi za to se oduzimaju. Dok rješavamo povrat, bodovi iz te narudžbe su na čekanju.", pendUnpaid: "čeka se uplata", pendUndelivered: "čeka se isporuka", actChanged: "Narudžba izmijenjena", actRefund: "Vraćen novac", nlTitle: "Bodove dobijaju pretplatnici newslettera", nlText: "Prijavite se na naš newsletter i vaše narudžbe će skupljati bodove.", nlBtn: "Prijavi se na newsletter", nlDone: "Hvala, prijava na newsletter je aktivna. Vaše narudžbe sada skupljaju bodove.", howNl: "Bodovi su za pretplatnike newslettera: vaše narudžbe skupljaju bodove dok ste prijavljeni na naš newsletter.", myPoints: "Moji bodovi", },
    hu: { goneAuto: "Eltávolította: {t}, ezért visszaadtunk {n} pontot.", removeBack: "Eltávolítás (+{n} pont)", addMore: "tegyen hozzá még {v}", apply: "Alkalmaz", applied: "alkalmazva", willEarn: "Ezzel a rendeléssel körülbelül {n} pontot gyűjt", haveIt: "Ez a jutalom már az Öné.", tooMany: "Már {n} fel nem használt jutalma van. Előbb használjon fel egyet.", valid: "{d} napon belül használható fel", points: "pont", title: "Az Ön jutalmai", bal: "{n} pont", 
      toGo: "még {n} pont", minSpend: "legalább {v} értékű rendelésnél", until: "eddig: {d}", applying: "Hozzáadás…",
      working: "Egy pillanat…", 
      gaveBack: "{n} pont visszaírva.",
      fail: "Nem sikerült: {e}", none: "Még nincs jutalom — gyűjtse tovább a pontokat.", 
      deadGone: "A(z) {c} már nem érvényes, kivettük a kosárból.", orphanTitle: "{t} már nem ingyenes.", orphanBody: "A termék a kosárban maradt, ára {v}.",
      orphanRemove: "Eltávolítás a kosárból", orphanKeep: "Marad és fizetem", notNow: "Egy pillanatra nem érhető el — próbálja újra.", unlock: "Még {v}, és feloldódik", loading: "Jutalmai betöltése…", actTitle: "Legutóbbi tevékenység", actOrder: "Rendelés {o}", actReward: "Jutalom: {t}", actAdd: "Pontok jóváírva", actRemove: "Pontok levonva", actClaw: "Rendelés törölve", actExpire: "Pontok lejártak", heroLabel: "Az Ön pontjai", nudgeNear: "Már csak {n} pont kell ehhez: {t}", nudgeReady: "Elég pontja van egy jutalomra — válasszon lent.", tabRewards: "Jutalmak", tabActivity: "Tevékenység", tabHow: "Hogyan működik", how1: "Minden elköltött {v} után {r} pont jár.", how2: "A pontokat a rendelés után írjuk jóvá. Ha a rendelést törlik, vagy termék kerül ki belőle, az ezért járó pontokat levonjuk.", how3: "A pontokat itt vagy a kosárban válthatja jutalomra. Meggondolta magát? Távolítsa el a jutalmat, és a pontok visszajárnak.", howExpiry: "Pontjai megmaradnak, ha legalább {m} havonta egyszer vásárol.", shopNow: "Vásárlás indítása", actNone: "Még nincs tevékenység — az első rendeléssel indul.", free: "INGYEN", save: "{v} megtakarítás", toBasket: "Tovább a kosárhoz", pendTotal: "+{n} pont úton van", pendOrder: "Rendelés {o} · úton", maxPc: "rendelésenként max. {n} db", pendFrom: "{d} után beváltható", howLock: "A rendelésért járó pontokat a kifizetés vagy kézbesítés után, de legkorábban a rendelés után {n} nappal használhatja fel. Addig „úton” lévőként látja őket.", actReturn: "Visszaküldött termék", pendHeld: "felfüggesztve, amíg a visszaküldést intézzük", howReturn: "Ha visszaküldi a terméket vagy visszakapja a pénzt, az érte járó pontokat levonjuk. Amíg a visszaküldést intézzük, a rendelés pontjai fel vannak függesztve.", pendUnpaid: "fizetésre vár", pendUndelivered: "kézbesítésre vár", actChanged: "Rendelés módosítva", actRefund: "Visszatérített összeg", nlTitle: "Pontokat a hírlevél-feliratkozók gyűjtenek", nlText: "Iratkozzon fel hírlevelünkre, és rendelései pontokat gyűjtenek.", nlBtn: "Feliratkozom a hírlevélre", nlDone: "Köszönjük, hírlevél-feliratkozása aktív. Rendelései mostantól pontokat gyűjtenek.", howNl: "A pontok a hírlevél-feliratkozóknak járnak: rendelései addig gyűjtenek pontot, amíg fel van iratkozva hírlevelünkre.", myPoints: "Pontjaim", },
    ro: { goneAuto: "Ați scos {t}, așa că v-am returnat {n} puncte.", removeBack: "Scoate (+{n} puncte)", addMore: "adăugați încă {v}", apply: "Aplică", applied: "aplicat", willEarn: "Pentru această comandă veți primi aproximativ {n} puncte", haveIt: "Aveți deja această recompensă.", tooMany: "Aveți deja {n} recompense nefolosite. Folosiți una mai întâi.", valid: "de folosit în {d} zile", points: "puncte", title: "Recompensele dvs.", bal: "{n} puncte", 
      toGo: "încă {n} puncte", minSpend: "la comenzi de minimum {v}", until: "până la {d}", applying: "Se adaugă…",
      working: "Un moment…", 
      gaveBack: "{n} puncte returnate.",
      fail: "Nu a funcționat: {e}", none: "Încă nicio recompensă — continuați să adunați puncte.", 
      deadGone: "{c} nu mai este valabil, l-am scos din coș.", orphanTitle: "{t} nu mai este gratuit.", orphanBody: "Produsul a rămas în coș și costă {v}.",
      orphanRemove: "Scoate din coș", orphanKeep: "Păstrez și plătesc", notNow: "Momentan indisponibil — încercați din nou.", unlock: "Adăugați încă {v} pentru a debloca", loading: "Se încarcă recompensele…", actTitle: "Activitate recentă", actOrder: "Comanda {o}", actReward: "Recompensă: {t}", actAdd: "Puncte adăugate", actRemove: "Puncte retrase", actClaw: "Comandă anulată", actExpire: "Puncte expirate", heroLabel: "Punctele dvs.", nudgeNear: "Încă doar {n} puncte până la {t}", nudgeReady: "Aveți destule puncte pentru o recompensă — alegeți mai jos.", tabRewards: "Recompense", tabActivity: "Activitate", tabHow: "Cum funcționează", how1: "Primiți {r} puncte pentru fiecare {v} cheltuiți.", how2: "Punctele se adaugă după comandă. Dacă o comandă este anulată sau un produs este scos din ea, punctele pentru acesta se retrag.", how3: "Schimbați punctele pe recompense aici sau în coș. V-ați răzgândit? Scoateți recompensa și punctele revin.", howExpiry: "Punctele rămân ale dvs. dacă faceți cel puțin o comandă la {m} luni.", shopNow: "Începeți cumpărăturile", actNone: "Încă nicio activitate — începe cu prima comandă.", free: "GRATUIT", save: "Economisiți {v}", toBasket: "Mergi la coș", pendTotal: "+{n} puncte pe drum", pendOrder: "Comanda {o} · pe drum", maxPc: "max. {n} buc. pe comandă", pendFrom: "utilizabile din {d}", howLock: "Punctele pentru o comandă pot fi folosite după ce este plătită sau livrată, dar cel mai devreme la {n} zile după comandă. Până atunci apar „pe drum”.", actReturn: "Produse returnate", pendHeld: "în așteptare până procesăm returul", howReturn: "Dacă returnați produse sau primiți banii înapoi, punctele pentru acestea se retrag. Cât timp procesăm returul, punctele comenzii sunt în așteptare.", pendUnpaid: "în așteptarea plății", pendUndelivered: "în așteptarea livrării", actChanged: "Comandă modificată", actRefund: "Bani returnați", nlTitle: "Punctele sunt pentru abonații la newsletter", nlText: "Abonați-vă la newsletterul nostru și comenzile dvs. vor aduna puncte.", nlBtn: "Mă abonez la newsletter", nlDone: "Vă mulțumim, abonamentul la newsletter este activ. Comenzile dvs. adună acum puncte.", howNl: "Punctele sunt pentru abonații la newsletter: comenzile dvs. adună puncte cât timp abonamentul la newsletterul nostru este activ.", myPoints: "Punctele mele", },
    pl: { goneAuto: "Usunięto {t}, więc zwróciliśmy {n} punktów.", removeBack: "Usuń (+{n} pkt)", addMore: "dodaj jeszcze {v}", apply: "Zastosuj", applied: "zastosowane", willEarn: "Za to zamówienie otrzymasz około {n} punktów", haveIt: "Masz już tę nagrodę.", tooMany: "Masz już {n} niewykorzystane nagrody. Najpierw użyj jednej.", valid: "do wykorzystania w {d} dni", points: "pkt", title: "Twoje nagrody", bal: "{n} pkt", 
      toGo: "brakuje {n} pkt", minSpend: "przy zamówieniu od {v}", until: "do {d}", applying: "Dodaję…",
      working: "Chwila…", 
      gaveBack: "Zwrócono {n} punktów.",
      fail: "Nie udało się: {e}", none: "Jeszcze brak nagród — zbieraj punkty dalej.", 
      deadGone: "{c} już nie działa, usunęliśmy go z koszyka.", orphanTitle: "{t} nie jest już za darmo.", orphanBody: "Produkt został w koszyku i kosztuje {v}.",
      orphanRemove: "Usuń z koszyka", orphanKeep: "Zostaw i zapłacę", notNow: "Chwilowo niedostępne — spróbuj ponownie.", unlock: "Dodaj jeszcze {v}, aby odblokować", loading: "Wczytujemy Twoje nagrody…", actTitle: "Ostatnia aktywność", actOrder: "Zamówienie {o}", actReward: "Nagroda: {t}", actAdd: "Punkty dodane", actRemove: "Punkty odjęte", actClaw: "Zamówienie anulowane", actExpire: "Punkty wygasły", heroLabel: "Twoje punkty", nudgeNear: "Jeszcze tylko {n} pkt do nagrody {t}", nudgeReady: "Masz dość punktów na nagrodę — wybierz poniżej.", tabRewards: "Nagrody", tabActivity: "Aktywność", tabHow: "Jak to działa", how1: "Za każde {v} zakupów otrzymujesz {r} punktów.", how2: "Punkty dodajemy po zamówieniu. Jeśli zamówienie zostanie anulowane lub usunięto z niego produkt, punkty za to odejmujemy.", how3: "Wymieniaj punkty na nagrody tutaj lub w koszyku. Zmiana zdania? Usuń nagrodę, a punkty wrócą.", howExpiry: "Punkty pozostają Twoje, jeśli kupujesz co najmniej raz na {m} miesięcy.", shopNow: "Zacznij zakupy", actNone: "Jeszcze brak aktywności — zacznie się od pierwszego zamówienia.", free: "GRATIS", save: "Oszczędzasz {v}", toBasket: "Przejdź do koszyka", pendTotal: "+{n} pkt w drodze", pendOrder: "Zamówienie {o} · w drodze", maxPc: "maks. {n} szt. na zamówienie", pendFrom: "do wykorzystania od {d}", howLock: "Punkty za zamówienie możesz wykorzystać, gdy jest opłacone lub dostarczone, ale najwcześniej {n} dni po zamówieniu. Do tego czasu widzisz je jako „w drodze”.", actReturn: "Zwrot towaru", pendHeld: "wstrzymane do rozpatrzenia zwrotu", howReturn: "Jeśli zwrócisz towar lub otrzymasz zwrot pieniędzy, punkty za to odejmiemy. Dopóki rozpatrujemy zwrot, punkty z tego zamówienia są wstrzymane.", pendUnpaid: "czeka na płatność", pendUndelivered: "czeka na dostawę", actChanged: "Zamówienie zmienione", actRefund: "Zwrot pieniędzy", nlTitle: "Punkty zbierają subskrybenci newslettera", nlText: "Zapisz się do naszego newslettera, a Twoje zamówienia będą zbierać punkty.", nlBtn: "Zapisz się do newslettera", nlDone: "Dziękujemy, subskrypcja newslettera jest aktywna. Twoje zamówienia zbierają teraz punkty.", howNl: "Punkty są dla subskrybentów newslettera: Twoje zamówienia zbierają punkty, dopóki subskrybujesz nasz newsletter.", myPoints: "Moje punkty", },
    de: { goneAuto: "Sie haben {t} entfernt, daher haben wir Ihnen {n} Punkte zurückgegeben.", removeBack: "Entfernen (+{n} Punkte)", addMore: "noch {v} hinzufügen", apply: "Einlösen", applied: "eingelöst", willEarn: "Mit dieser Bestellung sammeln Sie etwa {n} Punkte", haveIt: "Diese Prämie haben Sie bereits.", tooMany: "Sie haben bereits {n} offene Prämien. Lösen Sie zuerst eine ein.", valid: "innerhalb von {d} Tagen einlösbar", points: "Punkte", title: "Ihre Prämien", bal: "{n} Punkte", 
      toGo: "noch {n} Punkte", minSpend: "ab {v} Bestellwert", until: "bis {d}", applying: "Wird eingelöst…",
      working: "Einen Moment…", 
      gaveBack: "{n} Punkte gutgeschrieben.",
      fail: "Das hat nicht geklappt: {e}", none: "Noch keine Prämien — sammeln Sie weiter Punkte.", 
      deadGone: "{c} gilt nicht mehr, wir haben ihn aus dem Warenkorb genommen.", orphanTitle: "{t} ist nicht mehr kostenlos.", orphanBody: "Der Artikel blieb im Warenkorb und kostet {v}.",
      orphanRemove: "Aus dem Warenkorb nehmen", orphanKeep: "Behalten und zahlen", notNow: "Einen Moment nicht verfügbar — bitte erneut versuchen.", unlock: "Noch {v} bis zur Freischaltung", loading: "Ihre Prämien werden geladen…", actTitle: "Letzte Aktivität", actOrder: "Bestellung {o}", actReward: "Prämie: {t}", actAdd: "Punkte gutgeschrieben", actRemove: "Punkte abgezogen", actClaw: "Bestellung storniert", actExpire: "Punkte verfallen", heroLabel: "Ihre Punkte", nudgeNear: "Nur noch {n} Punkte bis {t}", nudgeReady: "Sie haben genug Punkte für eine Prämie — wählen Sie unten.", tabRewards: "Prämien", tabActivity: "Aktivität", tabHow: "So funktioniert es", how1: "Für je {v} Einkauf erhalten Sie {r} Punkte.", how2: "Punkte werden nach der Bestellung gutgeschrieben. Wird eine Bestellung storniert oder ein Produkt daraus entfernt, werden die Punkte dafür abgezogen.", how3: "Tauschen Sie Punkte hier oder im Warenkorb gegen Prämien. Anders überlegt? Entfernen Sie die Prämie und die Punkte kommen zurück.", howExpiry: "Ihre Punkte bleiben erhalten, solange Sie mindestens einmal in {m} Monaten einkaufen.", shopNow: "Jetzt einkaufen", actNone: "Noch keine Aktivität — sie beginnt mit Ihrer ersten Bestellung.", free: "GRATIS", save: "Sie sparen {v}", toBasket: "Zum Warenkorb", pendTotal: "+{n} Punkte unterwegs", pendOrder: "Bestellung {o} · unterwegs", maxPc: "max. {n} Stück pro Bestellung", pendFrom: "einlösbar ab {d}", howLock: "Punkte aus einer Bestellung können Sie einlösen, sobald sie bezahlt oder zugestellt ist, frühestens {n} Tage nach der Bestellung. Bis dahin sehen Sie sie als „unterwegs“.", actReturn: "Rücksendung", pendHeld: "angehalten, bis die Rücksendung bearbeitet ist", howReturn: "Wenn Sie Ware zurücksenden oder Geld zurückerhalten, werden die Punkte dafür abgezogen. Solange die Rücksendung bearbeitet wird, sind die Punkte dieser Bestellung angehalten.", pendUnpaid: "wartet auf Zahlung", pendUndelivered: "wartet auf Zustellung", actChanged: "Bestellung geändert", actRefund: "Geld erstattet", nlTitle: "Punkte sammeln Newsletter-Abonnenten", nlText: "Abonnieren Sie unseren Newsletter, und Ihre Bestellungen sammeln Punkte.", nlBtn: "Newsletter abonnieren", nlDone: "Vielen Dank, Ihr Newsletter-Abo ist aktiv. Ihre Bestellungen sammeln jetzt Punkte.", howNl: "Punkte gibt es für Newsletter-Abonnenten: Ihre Bestellungen sammeln Punkte, solange Sie unseren Newsletter abonniert haben.", myPoints: "Meine Punkte", },
  };
  STR.hr = STR.bs; STR.sr = STR.bs; // this shop runs on hr

  var LANG = "en";
  function t(key, vars) {
    var d = STR[LANG] || STR.en;
    var s = d[key] != null ? d[key] : (STR.en[key] || key);
    for (var k in (vars || {})) s = s.split("{" + k + "}").join(String(vars[k]));
    return s;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function up(s) { return String(s || "").toUpperCase(); }
  function money(cents, currency) {
    var n = Number(cents || 0) / 100;
    try { return new Intl.NumberFormat(LANG, { style: "currency", currency: currency || "EUR" }).format(n); }
    catch (e) { return n.toFixed(2) + " " + (currency || ""); }
  }
  function shortDate(iso) {
    try { return new Date(iso).toLocaleDateString(LANG === "en" ? "en-GB" : LANG, { day: "numeric", month: "numeric", year: "numeric" }); }
    catch (e) { return String(iso).slice(0, 10); }
  }

  // ─────────────────────────────── the theme ───────────────────────────────
  function routes() {
    var r = (window.Theme && window.Theme.routes) || {};
    return {
      update: r.cart_update_url || "/cart/update.js",
      change: r.cart_change_url || "/cart/change.js",
      add: r.cart_add_url || "/cart/add.js",
    };
  }
  function postJson(url, body) {
    return fetch(url, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (j) { return r.ok ? j : Promise.reject(new Error((j && (j.description || j.message)) || ("HTTP " + r.status))); });
    });
  }
  /** GET /cart.js. Rejects on failure — a failed read must never be read as "the basket is empty". */
  function readCart() {
    return fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { if (!r.ok) throw new Error("cart " + r.status); return r.json(); });
  }
  function codesOf(cart) {
    // `discount_codes` is undocumented but real, shaped [{code, applicable}]. A code that is present
    // but not applicable is not really in the basket.
    return (cart && cart.discount_codes || []).filter(function (d) { return d && d.code; });
  }
  function liveCodes(cart) { return codesOf(cart).filter(function (d) { return d.applicable !== false; }).map(function (d) { return up(d.code); }); }
  function allCodes(cart) { return codesOf(cart).map(function (d) { return up(d.code); }); }

  /** Hand the theme the section HTML we already got back, so it redraws its own cart in place. */
  function tellTheme(cart, sectionId) {
    try {
      var sections = (cart && cart.sections) || null;
      if (sections && sectionId && sections[sectionId]) {
        document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail: {
          resource: cart, sourceId: "amh-loyalty",
          data: { source: "amh-loyalty", itemCount: cart.item_count, sections: sections },
        } }));
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { sourceId: "amh-loyalty", reason: "amh-loyalty" } }));
      }
    } catch (e) { /* the theme will catch up on its own next change */ }
  }

  // ─────────────────────────────── the hub ───────────────────────────────
  function ident(el) {
    var d = el.dataset;
    return { host: d.host || location.host, cid: d.cid || "", email: d.email || "", d: d.d || "", sig: d.sig || "" };
  }
  function hubGet(el) {
    var i = ident(el), hub = (el.dataset.hub || "").replace(/\/$/, "");
    if (!hub || !i.cid || !i.sig) return Promise.resolve({ enabled: true, loggedIn: false });
    var q = Object.keys(i).map(function (k) { return k + "=" + encodeURIComponent(i[k]); }).join("&");
    return fetch(hub + "/api/public/loyalty/me?" + q, { credentials: "omit" }).then(function (r) { return r.json(); });
  }
  function hubPost(el, path, body) {
    var b = ident(el), hub = (el.dataset.hub || "").replace(/\/$/, "");
    for (var k in body) b[k] = body[k];
    return fetch(hub + path, { method: "POST", credentials: "omit", headers: { "Content-Type": "application/json" }, body: JSON.stringify(b) })
      .then(function (r) { return r.json(); });
  }
  /**
   * Tell the Hub which rewards we just drew. The ledger records what people TAKE; without this an
   * offer shown five hundred times and taken twice looks exactly like one shown twice and taken
   * twice, and nothing about the programme can ever be measured.
   *
   * Sent once per reward per placement per page load — a card redrawn when the basket changes is
   * the same sighting, not a new one — after the card is already on screen, with the answer thrown
   * away. sendBeacon where it exists, so a customer leaving the page does not lose it and does not
   * wait for it. This must never be able to slow down or break the card.
   */
  var SEEN = {};
  function reportSeen(el, placement, rewards) {
    try {
      var hub = (el.dataset.hub || "").replace(/\/$/, "");
      if (!hub || !rewards || !rewards.length) return;
      var fresh = [];
      for (var i = 0; i < rewards.length; i++) {
        var k = placement + "|" + rewards[i].rewardId;
        if (SEEN[k]) continue;
        SEEN[k] = 1;
        fresh.push(rewards[i]);
      }
      if (!fresh.length) return;
      var b = ident(el);
      b.placement = placement;
      b.rewards = fresh;
      // NOT sendBeacon. It always sends with credentials included, and a browser refuses any
      // cross-origin response that answers `Access-Control-Allow-Origin: *` to such a request — so
      // every one of these was blocked before it left, on every page load, and nothing was ever
      // recorded. fetch with keepalive does the same job (it outlives the page) and lets us say
      // plainly that we want no credentials on it: the identity is signed into the body.
      fetch(hub + "/api/public/loyalty/seen", {
        method: "POST", credentials: "omit", keepalive: true, mode: "cors",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(b),
      }).catch(function () { /* statistics are never worth a broken card */ });
    } catch (e) { /* statistics are never worth a broken card */ }
  }

  function rid() {
    var a = new Uint8Array(12);
    if (window.crypto && crypto.getRandomValues) crypto.getRandomValues(a);
    else for (var i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256);
    return Array.prototype.map.call(a, function (b) { return ("0" + b.toString(16)).slice(-2); }).join("");
  }

  // ─────────────────────────────── styling ───────────────────────────────
  // Colour and font come from the theme's tokens; the sizes are the drawer's own literals, so the
  // card sits flush with the discount box beside it. --card-corner-radius does NOT exist on this
  // shop, and --color-secondary-button-background is pure red — neither is used here.
  // Every selector is scoped to the custom tag. The theme has global rules for div, p, a, button and
  // svg, and a bare class selector loses to them; "amh-loyalty-card .x" wins and cannot leak out.
  // The sizes are the drawer's own literals (its discount field is 46px tall with a 6px radius and
  // a #dddddd border) so the card sits flush with the box beneath it.
  var CSS = [
    "amh-loyalty-card{display:block;font-family:var(--font-body--family,inherit);color:var(--color-foreground,#3a3a3a);font-size:15px;line-height:1.45;margin:0}",
    "amh-loyalty-card *{box-sizing:border-box}",
    "amh-loyalty-card p{margin:0}",
    // In the drawer the card sat hard against the left edge. Neither .cart-drawer__extras nor
    // .cart-actions carries any padding in this theme, so it gets its own — and only there: the
    // points page lays the card out itself.
    ".cart-actions amh-loyalty-card{padding-inline:16px}",
    "amh-loyalty-card .amh-h{display:flex;flex-wrap:wrap;gap:4px 12px;align-items:baseline;justify-content:space-between;",
    "  margin:0 0 16px;font-size:20px;line-height:34px;font-weight:500;color:var(--color-foreground,#3a3a3a)}",
    "amh-loyalty-card .amh-h>span{min-width:0;font-weight:500}",
    "amh-loyalty-card .amh-h>b{min-width:0;font-weight:500;white-space:nowrap}",
    "@media screen and (max-width:550px){amh-loyalty-card .amh-h{font-size:18px;margin-bottom:17px}}",
    // one row per voucher or reward: text on the left, one button on the right
    "amh-loyalty-card .amh-r:first-of-type{border-top:0;padding-top:0}",
    "amh-loyalty-card .amh-r{display:flex;gap:12px;align-items:center;justify-content:space-between;padding:12px 0;",
    "  border-top:1px solid var(--color-border,#dddddd)}",
    "amh-loyalty-card .amh-r__t{min-width:0;overflow-wrap:anywhere;font-size:16px;line-height:1.4;font-weight:500}",
    "amh-loyalty-card .amh-r__s{display:block;margin-top:4px;font-size:14px;font-weight:400;color:var(--color-foreground-muted,rgba(58,58,58,.62))}",
    "amh-loyalty-card .amh-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:700;letter-spacing:.5px;overflow-wrap:anywhere}",
    // The theme's .button is sized for "Add to cart" and "Checkout" — one per screen. Four of them
    // in a list of rewards outweighed the rewards themselves, so the card keeps the theme's shape
    // (font, radius) and takes its own, smaller, size. Two classes + the tag outrank .button alone.
    "amh-loyalty-card .amh-b.button{flex:none;white-space:nowrap;width:auto;height:auto;min-height:0;min-width:0;",
    "  padding:8px 16px;font-size:14px;line-height:1.3}",
    // TAKING a reward is the one thing the card wants pressed, so it alone carries the brand colours
    // (the same gradient as the points badge). Giving one back stays in the theme's plain button.
    "amh-loyalty-card .amh-b.button[data-get],amh-loyalty-card .amh-b.button[data-use]{border:0;color:#fff;",
    "  background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "amh-loyalty-card .amh-b.button[data-get]:hover,amh-loyalty-card .amh-b.button[data-use]:hover{color:#fff;filter:brightness(1.08)}",
    "amh-loyalty-card .amh-link{font:inherit;font-size:14px;background:none;border:0;padding:0;color:inherit;text-decoration:underline;cursor:pointer}",
    // Points are for newsletter subscribers: the one thing a non-subscriber is asked to do.
    "amh-loyalty-card .amh-nl{display:flex;flex-direction:column;gap:6px;margin:0 0 14px;padding:14px 16px;border-radius:10px;",
    "  font-size:14px;line-height:1.45;background:#fff7e6;border:1px solid #ffd591;color:#5c3d00}",
    "amh-loyalty-card .amh-nl b{font-size:15px}",
    "amh-loyalty-card .amh-nl .amh-b.button{align-self:flex-start;margin-top:4px;border:0;color:#fff;white-space:normal;",
    "  background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "amh-loyalty-card .amh-note{margin:10px 0 0;font-size:14px;color:var(--color-foreground-muted,rgba(58,58,58,.62))}",
    "amh-loyalty-card .amh-box{margin-top:10px;padding:10px 12px;border-radius:6px;font-size:13px;",
    "  background:rgba(128,128,128,.12);background:color-mix(in srgb,currentColor 7%,transparent)}",
    "amh-loyalty-card .amh-box b{display:block;margin-bottom:4px;font-size:14px}",
    "amh-loyalty-card .amh-row{display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-top:10px}",
    "amh-loyalty-card .amh-earn{margin:0 0 14px;padding:10px 12px;border-radius:6px;font-size:14px;",
    "  background:rgba(128,128,128,.12);background:color-mix(in srgb,currentColor 7%,transparent)}",
    "amh-loyalty-card .amh-foot{margin:14px 0 0;font-size:14px}",
    // The bar is a SPAN, and an inline element ignores height and vertical margins — so without
    // display:block it collapsed to nothing and has never once been drawn. And the track no longer
    // borrows the theme's foreground-rgb token: the theme redefines it to white in some
    // contexts, which would paint a white track on a white card. currentColor is the text of the
    // card it is sitting in, so it contrasts wherever the card is put.
    "amh-loyalty-card .amh-bar{display:block;height:8px;border-radius:4px;overflow:hidden;margin:8px 0 0;max-width:240px;",
    "  background:rgba(128,128,128,.22);background:color-mix(in srgb,currentColor 14%,transparent)}",
    "amh-loyalty-card .amh-bar>i{display:block;height:100%;border-radius:4px;transition:width .4s ease;",
    "  background:linear-gradient(90deg,#ff6b35 0%,#ff2d78 55%,#b23bff 100%)}",
    "amh-loyalty-card .amh-r__s b{font-weight:600;color:var(--color-foreground,inherit)}",
    // A REWARD YOU CAN SEE: the product's photo, what it saves, and what it costs in points.
    "amh-loyalty-card .amh-r__t{flex:1 1 auto;min-width:0}",
    "amh-loyalty-card .amh-th{flex:none;position:relative;width:56px;height:56px;border-radius:8px;overflow:hidden;background:#f5f5f5;",
    "  display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:700;text-decoration:none}",
    "amh-loyalty-card .amh-th img{width:100%;height:100%;object-fit:contain;display:block}",
    "amh-loyalty-card .amh-th--plain{background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "amh-loyalty-card .amh-pill{display:inline-block;padding:2px 8px;border-radius:999px;font-size:12px;font-weight:700;line-height:1.5;",
    "  color:#fff;background:#ff2d78;white-space:nowrap;vertical-align:middle}",
    "amh-loyalty-card .amh-price{display:block;margin-top:4px;font-size:14px}",
    "amh-loyalty-card .amh-price s{margin-right:6px;color:var(--color-foreground-muted,rgba(58,58,58,.62))}",
    "amh-loyalty-card .amh-price b{font-weight:600}",
    "amh-loyalty-card .amh-save{display:block;margin-top:2px;font-size:13px;font-weight:600;color:#1a8f4c}",
    "amh-loyalty-card .amh-pts{font-weight:600;color:var(--color-foreground,inherit)}",
    // On the points page the same rows become cards, two to a line.
    "amh-loyalty-card .amh-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin:16px 0 8px}",
    "@media screen and (max-width:550px){amh-loyalty-card .amh-grid{grid-template-columns:1fr}}",
    "amh-loyalty-card .amh-grid .amh-r{flex-direction:column;align-items:stretch;gap:0;padding:0;overflow:hidden;",
    "  border:1px solid var(--color-border,#dddddd);border-radius:var(--card-corner-radius,12px)}",
    "amh-loyalty-card .amh-grid .amh-r__t{padding:14px 14px 0}",
    "amh-loyalty-card .amh-grid .amh-r>.amh-b.button{margin:12px 14px 14px}",
    "amh-loyalty-card .amh-grid .amh-r>span:last-child{display:block;height:14px}",
    "amh-loyalty-card .amh-grid .amh-th{width:100%;height:auto;aspect-ratio:4/3;border-radius:0;font-size:34px}",
    "amh-loyalty-card .amh-grid .amh-th .amh-pill{position:absolute;top:10px;left:10px;font-size:14px;padding:4px 10px}",
    "amh-loyalty-card .amh-grid .amh-bar{max-width:none}",
    // ON A PHONE THE POINTS PAGE IS AN APP SCREEN. Valerian, 2.10.2026: 80-85% of orders come from
    // phones, and inside the programme the customer should "forget about everything else". So the
    // card takes the whole screen: its own top bar, tabs that stay put while the list scrolls, rows
    // sized for a thumb, and one button at the bottom that leads to the basket.
    "amh-loyalty-card .amh-appbar,amh-loyalty-card .amh-go{display:none}",
    "@media screen and (max-width:749px){",
    "amh-loyalty-card.amh-app{position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483000;display:block;overflow-y:auto;",
    "  -webkit-overflow-scrolling:touch;overscroll-behavior:contain;background:var(--color-background,#fff);",
    "  padding:0 16px calc(96px + env(safe-area-inset-bottom,0px))}",
    "amh-loyalty-card.amh-app .amh-appbar{display:flex;position:sticky;top:0;z-index:3;align-items:center;justify-content:space-between;",
    "  height:56px;margin:0 -16px;padding:0 6px;background:var(--color-background,#fff)}",
    "amh-loyalty-card.amh-app .amh-appbar__t{font-size:17px;font-weight:600}",
    "amh-loyalty-card.amh-app .amh-appbar__i{position:relative;width:44px;height:44px;display:flex;align-items:center;justify-content:center;",
    "  background:none;border:0;padding:0;color:inherit;text-decoration:none;cursor:pointer}",
    "amh-loyalty-card.amh-app .amh-appbar__n{position:absolute;top:3px;right:1px;min-width:18px;height:18px;border-radius:9px;padding:0 4px;",
    "  display:flex;align-items:center;justify-content:center;background:#ff2d78;color:#fff;font-size:11px;font-weight:700}",
    "amh-loyalty-card.amh-app .amh-hero{margin:4px 0 12px}",
    "amh-loyalty-card.amh-app .amh-tabs{position:sticky;top:56px;z-index:2;margin:0 -16px 4px;padding:0 16px;gap:0;background:var(--color-background,#fff)}",
    "amh-loyalty-card.amh-app .amh-tab{flex:1;padding:14px 4px;text-align:center}",
    "amh-loyalty-card.amh-app .amh-grid{grid-template-columns:1fr;gap:12px;margin-top:12px}",
    "amh-loyalty-card.amh-app .amh-grid .amh-r{display:grid;grid-template-columns:112px minmax(0,1fr);align-items:stretch}",
    "amh-loyalty-card.amh-app .amh-grid .amh-th{width:112px;height:100%;min-height:112px;aspect-ratio:auto;font-size:18px;text-align:center}",
    "amh-loyalty-card.amh-app .amh-grid .amh-th .amh-pill{top:8px;left:8px;font-size:12px;padding:2px 8px}",
    "amh-loyalty-card.amh-app .amh-grid .amh-r__t{padding:12px 12px 10px}",
    "amh-loyalty-card.amh-app .amh-grid .amh-r>.amh-b.button{grid-column:1 / -1;margin:12px;min-height:46px;font-size:16px}",
    "amh-loyalty-card.amh-app .amh-grid .amh-r>span:last-child{display:none}",
    "amh-loyalty-card.amh-app .amh-go{display:block;position:fixed;left:0;right:0;bottom:0;z-index:4;background:var(--color-background,#fff);",
    "  padding:10px 16px calc(10px + env(safe-area-inset-bottom,0px));box-shadow:0 -4px 16px rgba(0,0,0,.08)}",
    "amh-loyalty-card.amh-app .amh-go a{display:flex;align-items:center;justify-content:center;min-height:50px;border-radius:14px;color:#fff;",
    "  font-size:16px;font-weight:600;text-decoration:none;background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "}",
    "amh-loyalty-card .amh-r__t .amh-link{display:inline-block;margin-top:6px}",
    // The points page: a balance you can read from across the room, then three tabs.
    "amh-loyalty-card .amh-hero{border-radius:var(--card-corner-radius,14px);padding:22px 24px;color:#fff;margin:0 0 20px;",
    "  background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "amh-loyalty-card .amh-hero__k{display:block;font-size:14px;opacity:.9}",
    "amh-loyalty-card .amh-hero__n{display:block;font-size:44px;line-height:1.1;font-weight:600;margin:2px 0 0}",
    "amh-loyalty-card .amh-hero__n small{font-size:16px;font-weight:400;opacity:.9;margin-left:6px}",
    "amh-loyalty-card .amh-hero__t{display:block;font-size:14px;margin-top:14px}",
    "amh-loyalty-card .amh-hero .amh-bar{max-width:none;background:rgba(255,255,255,.3)}",
    "amh-loyalty-card .amh-hero .amh-bar>i{background:#fff}",
    "amh-loyalty-card .amh-tabs{display:flex;gap:4px;border-bottom:1px solid var(--color-border,#dddddd);margin:0 0 8px}",
    "amh-loyalty-card .amh-tab{appearance:none;background:none;border:0;border-bottom:3px solid transparent;margin:0 0 -1px;padding:10px 14px;",
    "  font:inherit;font-size:15px;cursor:pointer;color:var(--color-foreground-muted,rgba(58,58,58,.62))}",
    "amh-loyalty-card .amh-tab[aria-selected=true]{color:var(--color-foreground,inherit);font-weight:600;border-bottom-color:#ff2d78}",
    "amh-loyalty-card .amh-how{list-style:none;margin:8px 0 0;padding:0;counter-reset:amh}",
    "amh-loyalty-card .amh-how li{position:relative;padding:12px 0 12px 44px;font-size:15px;line-height:1.45;counter-increment:amh}",
    "amh-loyalty-card .amh-how li:before{content:counter(amh);position:absolute;left:0;top:10px;width:30px;height:30px;border-radius:50%;",
    "  display:flex;align-items:center;justify-content:center;color:#fff;font-weight:600;font-size:14px;",
    "  background:linear-gradient(135deg,#ff6b35,#ff2d78 55%,#b23bff)}",
    "amh-loyalty-card .amh-b.button.amh-cta{display:inline-block;margin-top:14px;text-decoration:none;border:0;color:#fff;",
    "  background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%)}",
    "amh-loyalty-card .amh-act{margin-top:8px}",
    "amh-loyalty-card .amh-pend{display:inline-flex;align-items:center;gap:6px;margin-top:10px;padding:4px 12px;border-radius:999px;",
    "  font-size:14px;font-weight:600;background:rgba(255,255,255,.22);color:#fff}",
    "amh-loyalty-card .amh-pend--c{margin:0 0 12px;background:#f1fbf5;color:#1a8f4c}",
    "amh-loyalty-card .amh-act__r--pend .amh-act__p{color:var(--color-foreground-muted,rgba(58,58,58,.62));font-weight:600}",
    "amh-loyalty-card .amh-act__r{display:flex;justify-content:space-between;gap:12px;padding:8px 0;font-size:14px;",
    "  border-top:1px solid var(--color-border,#dddddd)}",
    "amh-loyalty-card .amh-act__d{color:var(--color-foreground-muted,rgba(58,58,58,.62));margin-right:10px}",
    "amh-loyalty-card .amh-act__p{flex:none;font-weight:600;white-space:nowrap}",
    "amh-loyalty-card .amh-act__p--in{color:#1a8f4c}",
    "amh-loyalty-card .amh-load{display:flex;align-items:center;gap:12px;padding:18px 0;font-size:14px;",
    "  color:var(--color-foreground-muted,rgba(58,58,58,.62))}",
    "amh-loyalty-card .amh-dots{flex:none;display:block}",
    "amh-loyalty-card.amh-busy .amh-b.button{pointer-events:none;opacity:.5}",
    "amh-loyalty-card.amh-busy .amh-b.button.amh-b--wait{opacity:1;display:inline-flex;align-items:center;justify-content:center}",
    ".amh-cel__c{position:absolute;inset:0;display:block;width:100%;height:100%;max-width:none;pointer-events:none;z-index:3}",
    // the points page: the same rows, with room to breathe
    ".amh-page amh-loyalty-card{display:block;max-width:560px}",
    // ── the floating balance badge ──
    // Bottom RIGHT: bottom-left is the Hub's game button on this shop. Raised above the corner, not in
    // it: artmie.hu has a chat button there (about 56px, 20px from the edge), and the badge -- always on
    // top -- covered it, so nobody could open the chat once they had points (Shreya, 26.10.2026). 88px
    // clears it with a small gap; on shops without chat it simply floats a little higher.
    // The wrapper is display:contents
    // so the custom tag itself never occupies space in whatever the theme put it next to.
    "amh-loyalty-card[data-mode=badge]{display:contents}",
    ".amh-badge{position:fixed;right:16px;bottom:88px;z-index:2147482000;display:inline-flex;align-items:center;gap:7px;",
    "  padding:11px 17px;border-radius:999px;text-decoration:none;color:#fff;overflow:hidden;isolation:isolate;",
    "  font:700 14px/1 var(--font-body--family,system-ui,sans-serif);letter-spacing:.2px;",
    "  background:linear-gradient(135deg,#ff6b35 0%,#ff2d78 48%,#b23bff 100%);",
    "  box-shadow:0 6px 22px rgba(178,59,255,.38),0 2px 6px rgba(0,0,0,.18);",
    "  transition:transform .18s ease,box-shadow .18s ease}",
    ".amh-badge:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(178,59,255,.5),0 3px 8px rgba(0,0,0,.2)}",
    ".amh-badge:focus-visible{outline:2px solid #fff;outline-offset:2px}",
    // a slow shine sweeping across the pill
    ".amh-badge::after{content:\"\";position:absolute;inset:0;pointer-events:none;",
    "  background:linear-gradient(115deg,transparent 42%,rgba(255,255,255,.5) 50%,transparent 58%);",
    "  transform:translateX(-130%);animation:amhShine 4.2s ease-in-out infinite}",
    "@keyframes amhShine{0%,68%{transform:translateX(-130%)}100%{transform:translateX(130%)}}",
    // three little sparkles that twinkle out of step with each other
    ".amh-badge .amh-sp{position:absolute;pointer-events:none;font-size:9px;line-height:1;color:#fff;",
    "  opacity:0;animation:amhTwinkle 2.8s ease-in-out infinite}",
    ".amh-badge .amh-sp1{top:5px;left:12px;animation-delay:0s}",
    ".amh-badge .amh-sp2{bottom:5px;right:26px;font-size:7px;animation-delay:.9s}",
    ".amh-badge .amh-sp3{top:7px;right:12px;font-size:8px;animation-delay:1.8s}",
    "@keyframes amhTwinkle{0%,100%{opacity:0;transform:scale(.4)}45%{opacity:.95;transform:scale(1)}70%{opacity:0;transform:scale(.5)}}",
    ".amh-badge .amh-star{font-size:13px;line-height:1;filter:drop-shadow(0 1px 1px rgba(0,0,0,.25))}",
    // A modal dialog paints in the browser top layer, so nothing underneath can show through it —
    // the badge would simply vanish behind the open drawer. The theme marks that state on <html>,
    // so fade out cleanly instead of flickering. (base.css: html[scroll-lock]{overflow:hidden})
    "html[scroll-lock] .amh-badge{opacity:0;pointer-events:none;transition:opacity .15s}",
    "@media (prefers-reduced-motion:reduce){.amh-badge::after,.amh-badge .amh-sp{animation:none}.amh-badge .amh-sp{opacity:.8}}",
  ].join("");

  function addCss() {
    if (document.getElementById("amh-loyalty-css")) return;
    try { var s = document.createElement("style"); s.id = "amh-loyalty-css"; s.textContent = CSS; document.head.appendChild(s); } catch (e) { /* no head yet */ }
  }


  // ─────────────────────────────── the card ───────────────────────────────
  // state per card element, so two cards (drawer + page) never share anything
  /**
   * WHO THE CUSTOMER IS AND WHAT IS IN THEIR BASKET ARE FACTS ABOUT THE PAGE, NOT ABOUT AN ELEMENT.
   *
   * They used to live on each card, and a page carries several: the badge, the card in the drawer,
   * the card on the points page. Each fetched its own copy and kept it, so the moment one of them
   * changed the balance — taking a reward, handing one back — the others went on showing the number
   * they had loaded with. The badge sat at -100 while the card beside it said 300.
   *
   * One copy, shared. Anything that changes it re-draws every card on the page, so two of them can
   * never disagree again. `busy`, `writing` and the dismissed notices stay per element, because
   * those really are about one card.
   */
  var ME = null, ME_AT = 0, CART = null;

  function stateOf(el) {
    if (!el.__amh) el.__amh = { open: false, active: -1, busy: false, pending: null, dismissed: {}, orphan: null };
    return el.__amh;
  }

  /** Re-draw every card on the page from the shared copy. */
  function paintAll() {
    each(document.querySelectorAll("amh-loyalty-card"), function (el) {
      if (el.dataset.amhReady !== "1") return;
      try { el.dataset.mode === "badge" ? renderBadge(el) : render(el); } catch (e) { /* never throw into the storefront */ }
    });
  }



  /**
   * How far the basket still is from what this reward needs. A code can sit on a basket and
   * discount NOTHING: Shopify keeps it there and waits. "Applied" is then a lie, and the customer
   * is left looking at a free product they are being charged for.
   *
   * A free-product code measures the OTHER items in the basket — see planVoucher — so the gift's
   * own line does not count towards the minimum it is waiting for.
   */
  function shortfall(r) {
    if (!r.minSpend || !CART) return 0;
    var total = Number(CART.total_price || 0) / 100;
    if (r.gift && r.gift.variantId) {
      each(CART.items || [], function (l) {
        if (Number(l.variant_id) !== Number(r.gift.variantId)) return;
        total -= Number(l.final_line_price != null ? l.final_line_price : l.line_price || 0) / 100;
      });
    }
    return Math.max(0, Number(r.minSpend) - total);
  }

  /** The basket's progress towards a reward's minimum, as a bar and the amount still missing. */
  function basketBar(r, gap, ccy) {
    var pct = Math.max(4, Math.min(100, Math.round(100 * (Number(r.minSpend) - gap) / Math.max(1, Number(r.minSpend)))));
    return '<span class="amh-r__s"><b>' + esc(t("unlock", { v: money(Math.round(gap * 100), ccy) })) + "</b></span>" +
      '<span class="amh-bar"><i style="width:' + pct + '%"></i></span>';
  }

  /**
   * WHERE THE POINTS CAME FROM AND WENT. The page showed one number, so an order that had just
   * earned 105 points changed 341 to 446 and said nothing about why. The Hub has always sent the
   * last ten movements; this draws them. Page only — the drawer is a checkout column.
   */
  /** The number the customer knows: SMEMA files Shopify order 51509873 as MPBA51509873. */
  function orderNo(o) { return String(o || "").replace(/^MP[A-Z]{2}(?=\d)/, ""); }

  function activity(me) {
    var rows = (me.history || []).slice(0, 10), pend = me.pending || [];
    if (!rows.length && !pend.length) return '<p class="amh-note">' + esc(t("actNone")) + "</p>";
    var titles = {};
    each((me.vouchers || []).concat(me.dead || []), function (v) { if (v && v.code) titles[up(v.code)] = v.title; });
    var h = '<div class="amh-act">';
    each(pend, function (p) {
      h += '<div class="amh-act__r amh-act__r--pend"><span><span class="amh-act__d">' + esc(shortDate(p.at)) + "</span>" +
        esc(t("pendOrder", { o: p.order }) + (p.from ? " · " + t("pendFrom", { d: shortDate(p.from) }) : "")) +
        '</span><span class="amh-act__p">+' + esc(p.points) + " " + esc(t("points")) + "</span></div>";
    });
    each(rows, function (r) {
      var n = Number(r.points) || 0, what;
      if (r.kind === "EARN" && (r.until || r.held)) {
        h += '<div class="amh-act__r amh-act__r--pend"><span><span class="amh-act__d">' + esc(shortDate(r.at)) + "</span>" +
          esc(t("actOrder", { o: orderNo(r.order) }) + " · " + (r.held ? holdText(r.holdReason) : t("pendFrom", { d: shortDate(r.until) }))) +
          '</span><span class="amh-act__p">+' + esc(n) + " " + esc(t("points")) + "</span></div>";
        return;
      }
      if (r.kind === "EARN") what = t("actOrder", { o: orderNo(r.order) });
      else if (r.kind === "REDEEM") what = t("actReward", { t: titles[up(r.code)] || r.code || "" });
      else if (r.kind === "CLAWBACK") what = t(r.why === "return" ? "actReturn" : r.why === "amount" ? "actChanged" : r.why === "refund" ? "actRefund" : "actClaw") + (r.order ? " " + orderNo(r.order) : "");
      else if (r.kind === "EXPIRE") what = t("actExpire");
      else what = t(n >= 0 ? "actAdd" : "actRemove");
      h += '<div class="amh-act__r"><span><span class="amh-act__d">' + esc(shortDate(r.at)) + "</span>" + esc(what) + "</span>" +
        '<span class="amh-act__p' + (n > 0 ? " amh-act__p--in" : "") + '">' + (n > 0 ? "+" : "") + esc(n) + " " + esc(t("points")) + "</span></div>";
    });
    return h + "</div>";
  }

  /**
   * WHAT A REWARD SAVES, in money. Valerian, 2.10.2026: the customer must "immediately see how much
   * they will save" -- the product, a badge, the amount, the points. A product's price is printed
   * only when it is in the currency the card is showing: on a store that sells in several, the
   * Hub sends the shop's own, and a euro price next to a koruna symbol would be a wrong number.
   */
  function savingOf(r, ccy) {
    var g = r.gift, val = Number(r.value) || 0, save = null;
    var price = g && g.price != null && (!g.currency || up(g.currency) === up(ccy)) ? Number(g.price) : null;
    if (r.kind === "FREE_PRODUCT") save = price;
    else if (r.kind === "PERCENT") save = price != null ? Math.round(price * val) / 100 : null;
    else if (r.kind === "FIXED") save = price != null ? Math.min(val, price) : (val > 0 ? val : null);
    if (save == null || !(save > 0)) return null;
    return { save: save, was: price, now: price != null ? Math.max(0, price - save) : null };
  }
  function badgeOf(r, ccy) {
    var val = Number(r.value) || 0;
    if (r.kind === "FREE_PRODUCT" || r.kind === "FREE_SHIPPING") return t("free");
    if (r.kind === "PERCENT" && val > 0) return "−" + val + "%";
    if (r.kind === "FIXED" && val > 0) return "−" + money(Math.round(val * 100), ccy);
    return "";
  }
  /** The picture: the product's photo, or a tile in the brand colours for a reward with no product. */
  function media(r, badge, page) {
    var g = r.gift;
    if (g && g.image) {
      var img = '<img src="' + esc(g.image) + '" alt="' + esc(g.title || r.title) + '" loading="lazy">';
      var pill = page && badge ? '<span class="amh-pill">' + esc(badge) + "</span>" : "";
      return g.handle
        ? '<a class="amh-th" href="/products/' + esc(g.handle) + '">' + img + pill + "</a>"
        : '<span class="amh-th">' + img + pill + "</span>";
    }
    return '<span class="amh-th amh-th--plain">' + esc(page && badge ? badge : "★") + "</span>";
  }
  function priceLines(sv, ccy) {
    if (!sv) return "";
    var h = "";
    if (sv.was != null) {
      h += '<span class="amh-price"><s>' + esc(money(Math.round(sv.was * 100), ccy)) + "</s><b>" + esc(money(Math.round(sv.now * 100), ccy)) + "</b></span>";
    }
    return h + '<span class="amh-save">' + esc(t("save", { v: money(Math.round(sv.save * 100), ccy) })) + "</span>";
  }

  /**
   * The top of the points page: the balance, and ONE nudge — the nearest reward still out of reach,
   * with how far it is. A customer who can already take something is told that instead.
   */
  /**
   * POINTS ARE FOR NEWSLETTER SUBSCRIBERS (Valerian, 22.10.2026). A customer who is not on the list
   * collects nothing from new orders, so the card says so and offers the one click that fixes it.
   * The Hub decides who is subscribed (/me sends `newsletter`); nothing is guessed here.
   */
  function nlBanner(me) {
    var n = me && me.newsletter;
    if (!n || !n.required || n.subscribed) return "";
    return '<div class="amh-nl"><b>' + esc(t("nlTitle")) + "</b><span>" + esc(t("nlText")) + "</span>" +
      '<button type="button" class="amh-b button" data-nl>' + esc(t("nlBtn")) + "</button></div>";
  }

  function hero(me, held) {
    var near = null, ready = false;
    each(me.rewards || [], function (r) {
      if (held[r.id]) return;
      if (r.affordable) { ready = true; return; }
      if (!near || r.missingPoints < near.missingPoints) near = r;
    });
    var h = '<div class="amh-hero"><span class="amh-hero__k">' + esc(t("heroLabel")) + "</span>" +
      '<span class="amh-hero__n">' + esc(me.balance) + "<small>" + esc(t("points")) + "</small></span>";
    var pend = pendingLine(me);
    if (pend) h += '<span class="amh-pend">⏳ ' + esc(pend) + "</span>";
    if (near) {
      var pct = Math.max(4, Math.min(100, Math.round(100 * me.balance / Math.max(1, near.costPoints))));
      h += '<span class="amh-hero__t">' + esc(t("nudgeNear", { n: near.missingPoints, t: near.title })) + "</span>" +
        '<span class="amh-bar"><i style="width:' + pct + '%"></i></span>';
    } else if (ready) {
      h += '<span class="amh-hero__t">' + esc(t("nudgeReady")) + "</span>";
    }
    return h + "</div>";
  }

  /**
   * POINTS ON THE WAY: orders the customer has placed whose points the order scan has not booked
   * yet. Shown, never added to the balance -- the balance is only ever what the ledger says.
   */
  function pendingTotal(me) {
    var n = 0;
    each((me.pending || []).concat(me.locked || []), function (p) { n += Number(p.points) || 0; });
    return n;
  }
  /**
   * THE RETURN PERIOD. An order's points count at once but can be spent only some days after the
   * order (Valerian, 9.10.2026: otherwise order, spend, send it back). They are not in me.balance;
   * they arrive as me.locked (booked) and me.pending (not booked yet), each with the date it frees.
   * The pill says the total and the EARLIEST of those dates -- the moment something new is usable.
   */
  /** Why an order's points are on hold: waiting for the payment, for the delivery, or a return is open. */
  function holdText(reason) {
    if (reason === "UNPAID") return t("pendUnpaid");
    if (reason === "NOT_DELIVERED") return t("pendUndelivered");
    return t("pendHeld");
  }
  function pendingLine(me) {
    var n = pendingTotal(me), first = null;
    if (!(n > 0)) return "";
    var held = null;
    each((me.pending || []).concat(me.locked || []), function (p) {
      if (p.held && !held) held = p;
      if (p.from && (!first || new Date(p.from) < new Date(first))) first = p.from;
    });
    // RETURNS. An order with a claim open in the shop's system is on hold until the claim closes,
    // with no date to give; the activity list says which order it is.
    return t("pendTotal", { n: n }) + (first ? " · " + t("pendFrom", { d: shortDate(first) }) : held ? " · " + holdText(held.reason) : "");
  }

  function shopRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return r.charAt(r.length - 1) === "/" ? r : r + "/";
  }
  /** Shown on phones only (CSS): back to the shop, the screen's name, the basket with its count. */
  function appBar(cart) {
    var n = cart ? Number(cart.item_count) || 0 : 0;
    return '<div class="amh-appbar"><button type="button" class="amh-appbar__i" data-back aria-label="Back">' +
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>' +
      '<span class="amh-appbar__t">' + esc(t("myPoints")) + "</span>" +
      '<a class="amh-appbar__i" href="' + esc(shopRoot()) + 'cart" aria-label="' + esc(t("toBasket")) + '">' +
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>' +
      (n > 0 ? '<span class="amh-appbar__n">' + esc(n) + "</span>" : "") + "</a></div>";
  }
  function goBar(cart) {
    var n = cart ? Number(cart.item_count) || 0 : 0;
    if (!(n > 0)) return "";
    return '<div class="amh-go"><a href="' + esc(shopRoot()) + 'cart">' + esc(t("toBasket")) + " (" + esc(n) + ")</a></div>";
  }

  var TABS = ["rewards", "activity", "how"];
  function tabOf(st) {
    if (!st.tab) { try { st.tab = sessionStorage.getItem("amhTab") || ""; } catch (e) { /* private mode */ } }
    return TABS.indexOf(st.tab) >= 0 ? st.tab : "rewards";
  }
  function tabs(st) {
    var cur = tabOf(st), names = { rewards: "tabRewards", activity: "tabActivity", how: "tabHow" };
    var h = '<div class="amh-tabs" role="tablist">';
    each(TABS, function (k) {
      h += '<button type="button" class="amh-tab" role="tab" data-tab="' + k + '" aria-selected="' + (k === cur) + '">' + esc(t(names[k])) + "</button>";
    });
    return h + "</div>";
  }
  function showTab(el, k) {
    var st = stateOf(el);
    st.tab = k;
    try { sessionStorage.setItem("amhTab", k); } catch (e) { /* private mode */ }
    each(el.querySelectorAll("[data-tab]"), function (b) { b.setAttribute("aria-selected", String(b.getAttribute("data-tab") === k)); });
    each(el.querySelectorAll("[data-pane]"), function (p) { p.hidden = p.getAttribute("data-pane") !== k; });
  }

  /**
   * The rules, in the shop's own numbers. The rate is shown PER TEN of the currency so the sentence
   * reads naturally in every language ("10 points for every 10 KM"): "1 points" is wrong in English
   * and the Slavic languages each decline 1, 2 and 5 differently, but 10 is plural in all of them.
   */
  function how(me, ccy) {
    var per = Number(me.rate) * (Number(me.bonus) || 1) * 10;
    var h = '<ol class="amh-how">';
    if (per > 0) h += "<li>" + esc(t("how1", { r: Math.round(per * 10) / 10, v: money(1000, ccy) })) + "</li>";
    if (me.newsletter && me.newsletter.required) h += "<li>" + esc(t("howNl")) + "</li>";
    h += "<li>" + esc(t("how2")) + "</li>";
    if (Number(me.lockDays) > 0) h += "<li>" + esc(t("howLock", { n: me.lockDays })) + "</li>";
    h += "<li>" + esc(t("howReturn")) + "</li>";
    h += "<li>" + esc(t("how3")) + "</li>";
    if (Number(me.expiryMonths) > 0) h += "<li>" + esc(t("howExpiry", { m: me.expiryMonths })) + "</li>";
    return h + '</ol><a class="amh-b button amh-cta" href="/">' + esc(t("shopNow")) + "</a>";
  }

  function render(el) {
    var st = stateOf(el);
    var me = ME, cart = CART;
    if (!me || !me.enabled || !me.loggedIn) { el.classList.remove("amh-app"); el.innerHTML = ""; return; }

    var page = el.dataset.mode === "page";
    if (el.dataset.mode === "badge") { renderBadge(el); return; }
    var inBasket = cart ? allCodes(cart) : [];
    var rewards = me.rewards || [];
    var ccy = me.currency || el.dataset.currency || "";

    // ONE ROW PER REWARD. There used to be two lists — "your vouchers" and "get a new voucher" —
    // and the same reward appeared in both, which is what made the card unreadable: four entries
    // for two rewards. A reward now has exactly one row, and the row says where the customer is
    // with it. The "✓ in your basket" line that used to sit at the bottom is gone too; it belongs
    // on the row it is about.
    var held = {};
    each(me.vouchers || [], function (v) { if (v.rewardId && !held[v.rewardId]) held[v.rewardId] = v; });

    var h = page
      ? appBar(cart) + hero(me, held) + nlBanner(me) + tabs(st) + '<div data-pane="rewards"' + (tabOf(st) === "rewards" ? "" : " hidden") + ">"
      : '<div class="amh-h"><span>' + esc(t("title")) + "</span><b>" + esc(t("bal", { n: me.balance })) + "</b></div>" +
        (pendingLine(me) ? '<span class="amh-pend amh-pend--c">⏳ ' + esc(pendingLine(me)) + "</span>" : "") + nlBanner(me);

    // What this basket is worth in points, shown BEFORE they buy. Nykaa put this in their bag and
    // it is the strongest line on the screen: the reason to be in the programme, at the moment the
    // customer is deciding. `total_price` is after discounts and before delivery, which is the
    // closest the basket gets to the net figure the order scan will actually count — so the word
    // "about" is doing real work and must stay.
    if (NOTE && Date.now() - NOTE_AT < 20000) h += '<p class="amh-note" style="margin-top:0">' + esc(NOTE) + "</p>";

    // Not for someone the newsletter rule stops: this order would collect nothing, and the banner
    // above already says what to do about it.
    var nlStops = me.newsletter && me.newsletter.required && !me.newsletter.subscribed;
    if (!page && cart && Number(me.rate) > 0 && !nlStops) {
      var willEarn = Math.floor((Number(cart.total_price || 0) / 100) * Number(me.rate) * (Number(me.bonus) || 1));
      if (willEarn > 0) h += '<p class="amh-earn">✦ ' + esc(t("willEarn", { n: willEarn })) + "</p>";
    }

    /**
     * A voucher the customer holds but has not applied. NO CODE HERE: it was printing straight
     * after the title with nothing between them ("...50 mlPTS-M3YLCKZU"), and more to the point a
     * code earns its place only once it is applied, where it matches the discount line on the cart
     * total. Unused, it is machine noise on a row that should read like every other row.
     */
    function voucherLine(v) {
      return v.until ? '<span class="amh-r__s">' + esc(t("until", { d: shortDate(v.until) })) + "</span>" : "";
    }

    /** One reward, in whichever of its four states the customer is in. */
    function rewardRow(r) {
      var bd = badgeOf(r, ccy);
      var head = '<div class="amh-r">' + media(r, bd, page) + '<div class="amh-r__t"><b>' + esc(r.title) +
        (!page && bd ? ' <span class="amh-pill">' + esc(bd) + "</span>" : "") + "</b>" + priceLines(savingOf(r, ccy), ccy);
      var v = held[r.id];

      // Taken, and already on the basket: the only thing left to offer is taking it off again.
      // HOLDING IT — one action, whether or not it is on the basket. "Remove" has to mean what the
      // customer means by it: I do not want this. Taking the code off the basket and leaving them
      // still holding the reward, then asking them to press a second, differently-worded link to
      // get their points back, is two steps for one intention. So Remove gives the points back and
      // the reward returns to the top of the list, ready to take again.
      if (v) {
        var applied = inBasket.indexOf(up(v.code)) >= 0;
        var missing = applied ? shortfall(r) : 0;
        // HELD, BUT NOT ON THIS BASKET -- they took it and came back later, on another day or another
        // device. It used to offer only "Remove", so using a reward they already owned meant giving
        // it back and taking it again. Apply here costs no points: it is theirs already.
        if (!applied) {
          var need = shortfall(r);
          var back = '<button type="button" class="amh-link" data-give="' + esc(v.code) + '">' + esc(t("removeBack", { n: v.points })) + "</button>";
          if (need > 0) return head + voucherLine(v) + basketBar(r, need, ccy) + back + "</div><span></span></div>";
          return head + voucherLine(v) + back + "</div>" +
            '<button type="button" class="amh-b button" data-use="' + esc(v.code) + '">' + esc(t("apply")) + "</button></div>";
        }
        var state = applied
          ? (missing > 0 ? basketBar(r, missing, ccy)
            : '<span class="amh-r__s">✓ <span class="amh-code">' + esc(v.code) + "</span> " + esc(t("applied")) + "</span>")
          : voucherLine(v);
        return head + state + "</div>" +
          '<button type="button" class="amh-b button" data-give="' + esc(v.code) + '">' + esc(t("removeBack", { n: v.points })) + "</button></div>";
      }
      var sub = [];
      if (r.maxPieces) sub.push(t("maxPc", { n: r.maxPieces }));
      if (r.minSpend) sub.push(t("minSpend", { v: money(Math.round(r.minSpend * 100), ccy) }));
      if (r.validDays) sub.push(t("valid", { d: r.validDays }));
      var meta = '<b class="amh-pts">' + esc(r.costPoints) + " " + esc(t("points")) + "</b>" + (sub.length ? " · " + esc(sub.join(" · ")) : "");
      // THE BASKET HAS TO BE ABLE TO USE IT. A reward with a minimum order does nothing below that
      // minimum: Shopify keeps the code on the basket and discounts nothing. Offering "Apply" anyway
      // spent the points and, for a free product, put the product in the basket AT FULL PRICE — the
      // customer had just been "given" something and was being charged for it. So below the minimum
      // there is no button, only how much is missing and a bar that fills as the basket grows.
      var gap = r.affordable ? shortfall(r) : 0;
      if (gap > 0) {
        return head + '<span class="amh-r__s">' + meta + "</span>" + basketBar(r, gap, ccy) + "</div><span></span></div>";
      }
      if (r.affordable) {
        // Same reason as in redeem(): offering a button that cannot work is worse than saying so.
        if (r.kind === "FREE_PRODUCT" && !(r.gift && r.gift.variantId)) {
          return head + '<span class="amh-r__s">' + meta + " · " + esc(t("notNow")) + "</span></div><span></span></div>";
        }
        return head + '<span class="amh-r__s">' + meta + "</span></div>" +
          '<button type="button" class="amh-b button" data-get="' + esc(r.id) + '">' + esc(t("apply")) + "</button></div>";
      }
      var pct = Math.min(100, Math.round(100 * me.balance / Math.max(1, r.costPoints)));
      return head + '<span class="amh-r__s">' + meta + " · " + esc(t("toGo", { n: r.missingPoints })) + "</span>" +
        '<span class="amh-bar"><i style="width:' + pct + '%"></i></span></div><span></span></div>';
    }

    // In the drawer, which is a checkout column rather than a catalogue, the tail of things they
    // cannot afford yet is capped. Anything they hold or can take is always shown in full.
    var shown = rewards;
    if (!page) {
      shown = rewards.filter(function (r) { return held[r.id] || r.affordable; })
        .concat(rewards.filter(function (r) { return !held[r.id] && !r.affordable; }).slice(0, 3));
    }
    if (page) h += '<div class="amh-grid">';
    each(shown, function (r) { h += rewardRow(r); });
    if (page) h += "</div>";

    // A voucher whose reward has since been switched off still belongs to the customer, and would
    // otherwise vanish from a list built out of rewards. Same for a second voucher on one reward,
    // which the one-per-reward rule prevents now but older accounts still carry.
    var listed = {};
    each(shown, function (r) { if (held[r.id]) listed[up(held[r.id].code)] = 1; });
    each(me.vouchers || [], function (v) {
      if (listed[up(v.code)]) return;
      listed[up(v.code)] = 1;
      var applied = inBasket.indexOf(up(v.code)) >= 0;
      h += '<div class="amh-r"><div class="amh-r__t"><b>' + esc(v.title) + "</b>" +
        (applied ? '<span class="amh-r__s">✓ <span class="amh-code">' + esc(v.code) + "</span> " + esc(t("applied")) + "</span>" : voucherLine(v)) +
        "</div>" +
        '<button type="button" class="amh-b button" data-give="' + esc(v.code) + '">' + esc(t("removeBack", { n: v.points })) + "</button></div>";
    });

    if (!rewards.length && !(me.vouchers || []).length) {
      h += '<p class="amh-note" style="margin-top:0">' + esc(t("none")) + "</p>";
    }

    h += '<div data-slot></div>';

    h += '<p class="amh-note" data-msg hidden></p>';
    if (page) {
      h += '</div><div data-pane="activity"' + (tabOf(st) === "activity" ? "" : " hidden") + ">" + activity(me) + "</div>" +
        '<div data-pane="how"' + (tabOf(st) === "how" ? "" : " hidden") + ">" + how(me, ccy) + "</div>" + goBar(cart);
    }
    if (page) el.classList.add("amh-app");
    if (!page) h += '<p class="amh-foot"><a href="' + esc(el.dataset.page || "/pages/my-points") + '">' + esc(t("myPoints")) + " →</a></p>";

    st.writing = true;
    el.innerHTML = h;
    st.writing = false;
    wire(el);
    try { localStorage.setItem("amhCard", "1"); } catch (e) { /* private mode: no loader next time, nothing else */ }
    if (st.busy) showWait(el); else { st.wait = null; el.classList.remove("amh-busy"); }
    orphanCheck(el);
    strandedCheck(el);
    reportSeen(el, page ? "page" : "cart", shown.map(function (r) { return { rewardId: r.id, affordable: !!r.affordable }; }));
  }

  /**
   * THREE BOUNCING DOTS, DRAWN BY THE SVG ITSELF. Not a CSS animation on purpose: this theme switches
   * every CSS animation off for anyone whose system asks for reduced motion (which is what made the
   * first confetti invisible), and a wait with no sign of life reads as a broken page. SMIL moves
   * three dots a few pixels; it is feedback, not decoration.
   */
  function dots(colors) {
    var c = colors || ["#ff6b35", "#ff2d78", "#b23bff"], h = '<svg class="amh-dots" width="34" height="14" viewBox="0 0 34 14" aria-hidden="true">';
    for (var i = 0; i < 3; i++) {
      h += '<circle cx="' + (5 + i * 12) + '" cy="9" r="4" fill="' + c[i % c.length] + '">' +
        '<animate attributeName="cy" values="9;4;9;9" keyTimes="0;0.3;0.6;1" dur="0.9s" begin="' + (i * 0.15) + 's" repeatCount="indefinite"/></circle>';
    }
    return h + "</svg>";
  }

  /** The card before its data has arrived. It used to be simply empty, for as long as that took. */
  function renderLoading(el) {
    if (el.dataset.mode === "badge" || !el.dataset.cid) return;   // nobody logged in: there will be no card
    // Only in a browser that has been shown a card before. While the programme is limited to a list
    // of customers, everyone else gets no card at all — and "loading your rewards" appearing and
    // then vanishing would tell them about something they cannot have.
    try { if (localStorage.getItem("amhCard") !== "1") return; } catch (e) { return; }
    var st = stateOf(el);
    st.writing = true;
    el.innerHTML = '<div class="amh-load">' + dots() + "<span>" + esc(t("loading")) + "</span></div>";
    st.writing = false;
  }

  /**
   * The button that was pressed shows the dots until the work is done; the others dim and stop
   * taking clicks. Taking a reward is several round trips — the Hub issues a code at the shop, the
   * basket takes the product, then the code — and for those seconds the card used to look idle.
   */
  function waitOn(el, attr, val) {
    var st = stateOf(el);
    st.wait = { attr: attr, val: val };
    showWait(el);
  }
  function showWait(el) {
    var st = stateOf(el);
    if (!st.wait) { el.classList.remove("amh-busy"); return; }
    el.classList.add("amh-busy");
    each(el.querySelectorAll("[data-" + st.wait.attr + "]"), function (b) {
      if (b.getAttribute("data-" + st.wait.attr) !== st.wait.val || b.classList.contains("amh-b--wait")) return;
      b.style.minWidth = b.offsetWidth + "px";
      b.setAttribute("data-label", b.textContent);
      b.classList.add("amh-b--wait");
      b.innerHTML = dots(["currentColor"]);
    });
  }
  function clearWait(el) {
    var st = stateOf(el);
    st.wait = null;
    el.classList.remove("amh-busy");
    each(el.querySelectorAll(".amh-b--wait"), function (b) {
      b.classList.remove("amh-b--wait");
      b.textContent = b.getAttribute("data-label") || "";
      b.style.minWidth = "";
    });
  }

  function say(el, text) {
    // Every path that finishes without redrawing the card ends here with busy already false, so
    // this is where a button stops waiting; the paths that redraw are handled in render().
    if (!stateOf(el).busy) clearWait(el);
    var m = el.querySelector("[data-msg]");
    if (!m) return;
    m.hidden = !text; m.textContent = text || "";
  }
  function slot(el) { return el.querySelector("[data-slot]"); }

  function wire(el) {
    var st = stateOf(el);
    each(el.querySelectorAll("[data-nl]"), function (b) {
      b.addEventListener("click", function () { if (!st.busy) subscribeNl(el); });
    });
    each(el.querySelectorAll("[data-tab]"), function (b) {
      b.addEventListener("click", function () { showTab(el, b.getAttribute("data-tab")); });
    });
    each(el.querySelectorAll("[data-use]"), function (b) {
      b.addEventListener("click", function () {
        if (st.busy) return;
        var v = ((ME && ME.vouchers) || []).filter(function (x) { return up(x.code) === up(b.dataset.use); })[0];
        applyVoucher(el, b.dataset.use, v && v.gift);
        if (st.busy) waitOn(el, "use", b.dataset.use);
      });
    });
    each(el.querySelectorAll("[data-back]"), function (b) {
      b.addEventListener("click", function () {
        var same = false;
        try { same = !!document.referrer && new URL(document.referrer).host === location.host; } catch (e) { same = false; }
        if (same && history.length > 1) history.back(); else location.href = shopRoot();
      });
    });
    each(el.querySelectorAll("[data-give]"), function (b) {
      b.addEventListener("click", function () {
        if (st.busy) return;
        var v = ((ME && ME.vouchers) || []).filter(function (x) { return up(x.code) === up(b.dataset.give); })[0];
        if (v) { giveBack(el, v); if (st.busy) waitOn(el, "give", b.dataset.give); }
      });
    });
    each(el.querySelectorAll("[data-get]"), function (b) {
      b.addEventListener("click", function () {
        if (st.busy) return;
        var r = ((ME && ME.rewards) || []).filter(function (x) { return x.id === b.dataset.get; })[0];
        if (r) { redeem(el, r); if (st.busy) waitOn(el, "get", b.dataset.get); }
      });
    });
  }

  // ─────────────────────────────── spending ───────────────────────────────
  /** Inline yes/no, in the card. Never a browser alert, and never a second box on a second click. */


  /** The Subscribe button: join the newsletter, then redraw -- the banner goes and new orders earn. */
  function subscribeNl(el) {
    var st = stateOf(el);
    st.busy = true;
    say(el, t("working"));
    hubPost(el, "/api/public/loyalty/subscribe", {}).then(function (res) {
      st.busy = false;
      if (!res || !res.ok) { say(el, t("fail", { e: (res && res.error) || "" })); return; }
      say(el, "");
      note(t("nlDone"));
      refresh(el, true);
    }).catch(function () { st.busy = false; say(el, t("fail", { e: "network" })); });
  }

  function redeem(el, r) {
    var st = stateOf(el);
    // A FREE PRODUCT WE CANNOT NAME IS NOT A REWARD WE CAN GIVE. The gift comes from the Hub, which
    // resolves it at the shop; when that lookup fails the reward arrives with no product, and
    // taking it would spend the points on a code with nothing to discount. Shopify refuses such a
    // code outright, and the card would then hand the points back saying the customer removed
    // something they never had. Better to take nothing and say so.
    if (r && r.kind === "FREE_PRODUCT" && !(r.gift && r.gift.variantId)) { say(el, t("notNow")); return; }
    // The row is drawn from the basket as it was; the basket may have shrunk since. Same rule here.
    var gap = r ? shortfall(r) : 0;
    if (gap > 0) { say(el, t("unlock", { v: money(Math.round(gap * 100), (ME && ME.currency) || el.dataset.currency || "") })); paintAll(); return; }
    st.busy = true;
    say(el, t("working"));
    var requestId = rid();
    function attempt(n) {
      return hubPost(el, "/api/public/loyalty/redeem", { rewardId: r.id, requestId: requestId })
        .catch(function (e) { if (n > 0) return attempt(n - 1); throw e; }); // same requestId → one voucher, never two
    }
    attempt(1).then(function (res) {
      st.busy = false;
      if (!res || !res.ok) {
        say(el, res && res.code === "already_have" ? t("haveIt")
          : res && res.code === "too_many" ? t("tooMany", { n: res.max })
          : t("fail", { e: (res && res.error) || "" }));
        var s = slot(el); if (s) s.innerHTML = "";
        return;
      }
      // NO PANEL. It covered the whole drawer and read as a page of its own, to tell the customer
      // two things they already have: the code, which the row beneath now shows and which is
      // e-mailed anyway, and an OK button to dismiss something they never asked to open.
      // The confetti is the celebration; the row is the confirmation.
      cheer(el);
      applyVoucher(el, res.code, r && r.gift, true);
    }).catch(function () { st.busy = false; say(el, t("fail", { e: "network" })); });
  }

  // ─────────────────────────────── the celebration ───────────────────────────────
  var CONFETTI = ["#ff6b35", "#ff2d78", "#b23bff", "#feb21f", "#00b8a9"];

  /**
   * The confetti with no panel around it, for the moment the discount actually reaches the basket.
   * That is the moment the customer is waiting for — until then the voucher is only a promise, and
   * a code they cannot see working. It goes in the dialog, like the panel does, so the drawer never
   * reads it as a click outside itself, and it takes itself away when the drawing is over.
   */
  function cheer(el) {
    try {
      var host = el.closest("dialog") || el;
      if (getComputedStyle(host).position === "static") host.style.position = "relative";
      var old = host.querySelector(":scope > canvas.amh-cel__c");
      if (old) old.remove();                     // one burst at a time, not two overlapping
      var cv = document.createElement("canvas");
      cv.className = "amh-cel__c";
      cv.setAttribute("aria-hidden", "true");
      host.appendChild(cv);
      confetti(cv);
    } catch (e) { /* decoration only: never let it break applying a discount */ }
  }

  /**
   * CONFETTI, ON A CANVAS. The CSS version was invisible for two reasons that kept coming back, so
   * this one depends on neither: requestAnimationFrame keeps running whatever the operating system
   * says about animation effects, and no theme rule can reach inside a canvas.
   *
   * Two sources, because one never fills the panel: a burst out of the middle, which is the part the
   * eye actually catches, and a rain from above that keeps arriving while the customer reads their
   * code. Someone who has turned animations off gets fewer, slower pieces rather than none — they
   * should still see that something good just happened.
   */
  function confetti(cv) {
    if (!cv || !cv.getContext) return;   // also the case in the headless check, which has no canvas
    var ctx;
    try { ctx = cv.getContext("2d"); } catch (e) { return; }
    if (!ctx) return;

    var calm = false;
    try { calm = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion:reduce)").matches); } catch (e) { /* assume motion is welcome */ }
    var GRAV = calm ? 150 : 460;     // px/s2, how hard a piece is pulled down
    var LIFE = calm ? 3400 : 3000;   // ms before the whole thing fades out
    var w = 1, h = 1, bits = [], started = 0, last = 0, tries = 0;

    function measure() {
      var r = cv.getBoundingClientRect();
      w = Math.max(1, Math.round(r.width)); h = Math.max(1, Math.round(r.height));
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return r.width > 1 && r.height > 1;
    }

    function piece(burst) {
      var a, sp;
      if (burst) { a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4; sp = (calm ? 90 : 230) + Math.random() * (calm ? 90 : 240); }
      else { a = Math.PI / 2; sp = (calm ? 30 : 70) + Math.random() * (calm ? 40 : 90); }
      return {
        x: burst ? w / 2 + (Math.random() - 0.5) * 40 : Math.random() * w,
        y: burst ? h * 0.52 : -20 - Math.random() * h * 0.8,
        vx: Math.cos(a) * sp + (Math.random() - 0.5) * 60,
        vy: Math.sin(a) * sp,
        bw: 5 + Math.random() * 5, bh: 8 + Math.random() * 8,
        rot: Math.random() * Math.PI * 2, vr: (Math.random() - 0.5) * (calm ? 3 : 9),
        c: CONFETTI[Math.floor(Math.random() * CONFETTI.length)]
      };
    }

    function frame(now) {
      if (!cv.isConnected) return;   // the panel was closed: stop, and leave no loop running
      if (!started) { started = now; last = now; }
      var age = now - started, dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // the rain keeps arriving for the first two thirds, so the panel is never briefly bare
      if (age < LIFE * 0.66 && bits.length < (calm ? 40 : 110) && Math.random() < (calm ? 0.35 : 0.8)) bits.push(piece(false));

      var fade = age > LIFE ? Math.max(0, 1 - (age - LIFE) / 600) : 1;
      if (!fade) { try { cv.remove(); } catch (e) { /* already gone */ } return; }

      ctx.clearRect(0, 0, w, h);
      ctx.globalAlpha = fade;
      for (var i = 0; i < bits.length; i++) {
        var b = bits[i];
        b.vy += GRAV * dt; b.vx *= 0.995;
        b.x += b.vx * dt; b.y += b.vy * dt; b.rot += b.vr * dt;
        ctx.save(); ctx.translate(b.x, b.y); ctx.rotate(b.rot);
        ctx.fillStyle = b.c; ctx.fillRect(-b.bw / 2, -b.bh / 2, b.bw, b.bh);
        ctx.restore();
      }
      bits = bits.filter(function (b) { return b.y < h + 40; });
      requestAnimationFrame(frame);
    }

    // THE DRAWER IS STILL SLIDING OPEN when we get here, so the box can measure nothing at all. The
    // old code read that zero as the fall distance, which is why the pieces only ever appeared in a
    // band at the top. Wait for a real size instead.
    (function begin() {
      if (!cv.isConnected) return;
      if (!measure() && tries++ < 30) { requestAnimationFrame(begin); return; }
      for (var i = 0; i < (calm ? 18 : 44); i++) bits.push(piece(true));
      requestAnimationFrame(frame);
    })();

    try { window.addEventListener("resize", measure, { passive: true, once: true }); } catch (e) { /* fine */ }
  }

  // ─────────────────────────────── cart actions ───────────────────────────────
  function sectionIdOf(el) {
    try {
      var n = el.closest("[data-section-id]") || (el.closest("dialog") || document).querySelector("[data-section-id]");
      return (n && n.dataset.section_id) || (n && n.dataset.sectionId) || null;
    } catch (e) { return null; }
  }

  /**
   * Put a voucher in the basket. For a free-product voucher the gift line must go in FIRST — Shopify
   * refuses a buy-X-get-Y code when the item it discounts is not there.
   */
  /** `quiet` suppresses the burst: the celebration panel is already showing one of its own. */
  function applyVoucher(el, code, gift, quiet) {
    var st = stateOf(el);
    if (st.busy) return Promise.resolve();
    st.busy = true;
    say(el, t("applying"));
    var R = routes(), sid = sectionIdOf(el);
    var chain = Promise.resolve();
    if (gift && gift.variantId) {
      // ONLY IF IT IS NOT THERE ALREADY. The line used to be added on every Apply, so a customer who
      // had the product in the basket and then took its reward ended up with two -- and paid for
      // the second. The reward needs the product present; it does not need another one.
      chain = chain.then(function () { return readCart(); }).then(function (cart) {
        var want = Number(gift.productId || 0), there = false;
        each((cart && cart.items) || [], function (l) {
          if (Number(l.variant_id) === Number(gift.variantId) || (want && Number(l.product_id) === want)) there = true;
        });
        return there ? null : postJson(R.add, { items: [{ id: gift.variantId, quantity: 1 }] });
      });
    }
    return chain
      .then(function () { return readCart(); })                 // re-read: never build a write off a stale list
      .then(function (cart) {
        var keep = liveCodes(cart).filter(function (c) { return c !== up(code); });
        keep.push(up(code));
        var body = { discount: keep.join(","), sections_url: location.pathname };
        if (sid) body.sections = [sid];
        return postJson(R.update, body);
      })
      .then(function (cart) {
        st.busy = false;
        tellTheme(cart, sid);
        // Only when Shopify actually took it. A code can come back in the basket and still be
        // refused — a buy-X-get-Y with the product missing, or a minimum the basket has not met —
        // and celebrating a discount that is not really there is worse than not celebrating.
        var landed = liveCodes(cart).indexOf(up(code)) >= 0;
        if (landed && !quiet) cheer(el);
        refresh(el, true);
        return landed;
      })
      .catch(function (e) {
        st.busy = false;
        say(el, t("fail", { e: (e && e.message) || "" }));
        refresh(el, true);
        return false;
      });
  }



  /**
   * Take a line off the basket, finding it FRESH by its variant. A key out of a cart we read
   * earlier can be stale — the line may have been merged with another, re-added, or had its
   * quantity changed since — and Shopify answers a stale key with "no valid ID or line parameter",
   * which is exactly what the shop showed. The variant id never goes stale.
   */
  function removeLine(el, variantId) {
    var st = stateOf(el);
    if (st.busy) return;
    st.busy = true;
    var R = routes(), sid = sectionIdOf(el), want = Number(variantId);
    readCart()
      .then(function (cart) {
        var line = null;
        each(CART.items || [], function (l) { if (!line && Number(l.variant_id) === want) line = l; });
        if (!line) return cart;   // already gone: nothing to remove, and nothing to apologise for
        var body = { id: line.key, quantity: 0, sections_url: location.pathname };
        if (sid) body.sections = [sid];
        return postJson(R.change, body);
      })
      .then(function (cart) { st.busy = false; tellTheme(cart, sid); refresh(el, true); })
      .catch(function (e) { st.busy = false; say(el, t("fail", { e: (e && e.message) || "" })); });
  }

  /**
   * Codes that are no longer valid but still sitting on the basket. Shopify leaves them there until
   * something takes them off, so an expired or cancelled voucher keeps drawing its discount.
   */
  /** Dead codes we have already tried to take off the basket, so we never chase one twice. */
  var PURGED = {};

  function purge(el) {
    var st = stateOf(el);
    if (!ME || !CART || st.busy) return false;
    var dead = (ME.dead || []).map(function (d) { return up(d && d.code); }).filter(Boolean);
    // Only codes we have not already tried. If Shopify will not let go of one — it is not really
    // on the basket, the write is refused, anything — retrying it forever would mean a cart write
    // on every refresh, for the rest of the visit.
    var here = allCodes(CART).filter(function (c) { return dead.indexOf(c) >= 0 && !PURGED[c]; });
    if (!here.length) return false;
    each(here, function (c) { PURGED[c] = 1; });
    st.busy = true;
    var R = routes(), sid = sectionIdOf(el);
    readCart()
      .then(function (cart) {
        var keep = allCodes(cart).filter(function (c) { return dead.indexOf(c) < 0; });
        var body = { discount: keep.join(","), sections_url: location.pathname };
        if (sid) body.sections = [sid];
        return postJson(R.update, body);
      })
      .then(function (cart) {
        st.busy = false;
        say(el, t("deadGone", { c: here[0] }));
        tellTheme(cart, sid);
        refresh(el, true);
      })
      .catch(function () { st.busy = false; });
    return true;
  }

  /**
   * A free product whose voucher has gone. The line stays in the basket at full price — Shopify
   * never removes it — so the shopper is told and given the choice. Never silent, either way.
   */
  /** Offers already handed back, so a stranded one is settled once and never chased again. */
  var HANDLED = {};

  /**
   * A line the card shows for a short while and then forgets. It cannot live in the message slot:
   * giving points back triggers a redraw, and a redraw empties that slot — so the one sentence
   * explaining what just happened would vanish in the same instant it was written.
   */
  var NOTE = null, NOTE_AT = 0;
  function note(text) { NOTE = text; NOTE_AT = Date.now(); }

  /**
   * THE MIRROR OF orphanCheck. There the voucher died and its product stayed; here the voucher is
   * alive and the PRODUCT has gone — they emptied the line the reward was pointing at. Shopify keeps
   * a percentage code sitting on the basket discounting nothing, and refuses a free-product one
   * outright, and either way the customer is holding something that has stopped meaning anything.
   *
   * We cannot ask before the removal: the trash button on a cart line belongs to the theme. So we
   * notice afterwards, and offer the two answers that are actually available.
   */
  /**
   * Is ANY card mid-write? `busy` is per element, and a page carries two — the one in the drawer and
   * the one on My points. The drawer card adding a gift line left the page card entirely unaware,
   * free to read a half-finished basket and act on it.
   */
  function anyBusy() {
    var b = false;
    each(document.querySelectorAll("amh-loyalty-card"), function (n) { if (n.__amh && n.__amh.busy) b = true; });
    return b;
  }

  function strandedCheck(el) {
    if (!ME || !CART || anyBusy()) return;
    var here = allCodes(CART), live = liveCodes(CART);
    var items = CART.items || [];
    var found = null;
    each(ME.vouchers || [], function (v) {
      if (found || !v.gift || HANDLED[up(v.code)]) return;
      if (here.indexOf(up(v.code)) < 0) return;                 // not on the basket: nothing stranded
      // SHOPIFY'S OWN VERDICT FIRST, for a free product. It REFUSES a buy-X-get-Y code whose item is
      // not in the basket, so `applicable` is the shop saying the voucher has stopped working.
      // While it is still applicable the voucher is doing its job, whatever our list of lines says —
      // and this check CANCELS a voucher, so it must never be the first to decide.
      if (v.kind === "FREE_PRODUCT" && live.indexOf(up(v.code)) >= 0) return;
      var want = Number(v.gift.productId || 0), wantV = Number(v.gift.variantId || 0);
      var present = false;
      each(items, function (l) {
        if (want && Number(l.product_id) === want) present = true;
        else if (!want && wantV && Number(l.variant_id) === wantV) present = true;
      });
      if (!present) found = v;
    });
    if (!found || anyBusy() || HANDLED[up(found.code)]) return;
    HANDLED[up(found.code)] = 1;
    note(t("goneAuto", { t: (found.gift && found.gift.title) || found.title, n: found.points }));
    giveBack(el, found);
  }

  /**
   * Is the basket giving this line away RIGHT NOW? Shopify's own number for the line, and the only
   * thing that can settle the question: our copy of /me is a cache, and at the one moment that
   * matters most — the instant a free product is redeemed — it is always a step behind.
   */
  function lineIsFree(l) {
    var v = l.final_line_price != null ? l.final_line_price : l.line_price;
    return Number(v) === 0;
  }

  /** "Keep and pay" has to outlive the page, or the same question is asked again on the next view. */
  function kept(k) {
    try { return localStorage.getItem("amhKeep:" + k) === "1"; } catch (e) { return false; }
  }

  function findOrphan(el) {
    var st = stateOf(el);
    // ONLY A FREE PRODUCT can stop being free. Since a percent or money-off reward may name a
    // product too, every dead voucher now carries a `gift` — and this notice started telling
    // customers that a product they had taken 50% off "is no longer free", which it never was.
    var gifts = (ME.dead || []).filter(function (d) { return d.kind === "FREE_PRODUCT" && d.gift && d.gift.variantId; });
    if (!gifts.length) return null;
    // A LIVE voucher for the same product means that line is NOT an orphan — it is the gift for the
    // voucher being used right now. Matching on the variant alone made a voucher cancelled weeks ago
    // offer to take away the free product of the one in the basket today, and the customer lost it.
    var alive = {};
    each(ME.vouchers || [], function (v) { if (v.gift && v.gift.variantId) alive[Number(v.gift.variantId)] = 1; });
    var items = CART.items || [];
    for (var i = 0; i < gifts.length; i++) {
      var g = gifts[i];
      if (alive[Number(g.gift.variantId)]) continue;
      for (var j = 0; j < items.length; j++) {
        var line = items[j];
        if (Number(line.variant_id) !== Number(g.gift.variantId)) continue;
        // THE BASKET OVERRULES US. A line that costs nothing has not stopped being free, whatever
        // our voucher list says. Without this, redeeming a free product showed the panel in the
        // same breath: the gift line had just been added, the new voucher had not reached our copy
        // of /me yet, and an older dead voucher for the same product matched it. The panel printed
        // its own refutation — "no longer free … and now costs 0,00 BAM".
        if (lineIsFree(line)) continue;
        var k = line.key + ":" + g.code;
        if (st.dismissed[k] || kept(k)) continue;
        return { g: g, line: line, key: k };
      }
    }
    return null;
  }

  function orphanCheck(el) {
    var st = stateOf(el), s = slot(el);
    if (!ME || !CART || !s) return;
    var found = findOrphan(el);
    if (!found) {
      // AND IT MUST COME DOWN AGAIN. The panel used to be written once and then left alone, because
      // the check refused to look at a slot that already had something in it. So a question that
      // had answered itself stayed on screen: the product was free again and the card was still
      // asking whether to pay for it.
      if (st.orphan) { st.orphan = null; s.innerHTML = ""; }
      return;
    }
    if (st.orphan === found.key) return;      // already asking exactly this, leave it alone
    if (s.innerHTML && !st.orphan) return;    // something else is using the slot
    st.orphan = found.key;
    showOrphan(el, s, found.g, found.line, found.key);
  }
  function showOrphan(el, s, g, line, key) {
    var st = stateOf(el);
    s.innerHTML = '<div class="amh-box"><b>' + esc(t("orphanTitle", { t: g.gift.title || line.product_title })) + "</b>" +
      esc(t("orphanBody", { v: money(line.final_line_price != null ? line.final_line_price : line.line_price, CART.currency) })) +
      '<div class="amh-row"><button type="button" class="amh-b button" data-rm>' + esc(t("orphanRemove")) + "</button>" +
      '<button type="button" class="amh-link" data-keep>' + esc(t("orphanKeep")) + "</button></div></div>";
    s.querySelector("[data-rm]").addEventListener("click", function () {
      st.orphan = null; s.innerHTML = "";
      removeLine(el, g.gift.variantId);
    });
    s.querySelector("[data-keep]").addEventListener("click", function () {
      st.dismissed[key] = 1;
      // Written AND read (see kept): it used to be written only, so "keep and pay" was forgotten on
      // the next page view, and the card asked again. It also carries the answer from the drawer to
      // the My points page, which is a second card with its own memory.
      try { localStorage.setItem("amhKeep:" + key, "1"); } catch (e) { /* private mode */ }
      st.orphan = null; s.innerHTML = "";
    });
  }

  // ─────────────────────────────── giving one back ───────────────────────────────
  // NO CONFIRMATION, on this or on taking a reward. The two are each other's undo: take one by
  // mistake and this hands the points straight back; hand one back by mistake and you take it
  // again. Nothing is lost either way, so there is nothing to confirm — and asking put a grey box
  // at the foot of the card that read like a third item in the list.
  //
  // Only offered while the code is NOT on the basket: cancelling one that is applied switches it
  // off while the basket still shows its discount.
  function giveBack(el, v) {
    var st = stateOf(el);
    if (st.busy) return;
    st.busy = true;
    say(el, t("working"));
    hubPost(el, "/api/public/loyalty/cancel", { code: v.code }).then(function (r) {
      st.busy = false;
      if (r && r.ok) { say(el, t("gaveBack", { n: r.points })); refresh(el, true); }
      else say(el, t("fail", { e: (r && r.error) || "" }));
    }).catch(function () { st.busy = false; say(el, t("fail", { e: "network" })); });
  }


  // ─────────────────────────────── loading + mounting ───────────────────────────────
  var ME_TTL_MS = 20000;
  function refresh(el, force) {
    var st = stateOf(el);
    var fresh = ME && !force && (Date.now() - ME_AT < ME_TTL_MS);
    var jobs = [fresh ? Promise.resolve(ME) : hubGet(el).then(function (m) { ME_AT = Date.now(); return m; }),
      readCart().catch(function () { return null; })];
    return Promise.all(jobs).then(function (both) {
      ME = both[0] || ME;
      if (both[1]) CART = both[1];
      // EVERY card, not the one that asked. A page carries the badge and one or two cards, and the
      // balance one of them just changed is the balance all of them show.
      if (!ME || !ME.enabled || !ME.loggedIn) {
        each(document.querySelectorAll("amh-loyalty-card"), function (n) { n.classList.remove("amh-app"); n.innerHTML = ""; });
        return;
      }
      // Draw FIRST, always. purge() takes dead codes off the basket and refreshes again when it
      // lands, and this used to return before painting so the card would not flicker in between.
      // But a card that never repaints is far worse than one that repaints twice, and that is
      // exactly what happened: with fifteen dead codes from an evening of applying and returning,
      // purge fired on every refresh and the card never drew again until a hard reload.
      paintAll();
      purge(el);
    }).catch(function () {
      // never throw into the storefront — and never leave "loading" on screen for a load that failed
      if (!ME) each(document.querySelectorAll("amh-loyalty-card .amh-load"), function (n) { n.parentNode.innerHTML = ""; });
    });
  }

  /**
   * The floating balance badge. It is a plain link to the points page, not a panel: the card in the
   * cart sidebar already does the spending, and a second copy of it would be one more thing to keep
   * in step. Drawn only when there is something to show — a pill reading "0" is just clutter.
   */
  function renderBadge(el) {
    var st = stateOf(el), me = ME;
    if (!me || !me.enabled || !me.loggedIn) { el.innerHTML = ""; return; }
    if (!(me.balance > 0) && !(me.vouchers || []).length) { el.innerHTML = ""; return; }
    st.writing = true;
    el.innerHTML = '<a class="amh-badge" href="' + esc(el.dataset.page || "/pages/my-points") + '">' +
      '<span class="amh-sp amh-sp1" aria-hidden="true">✦</span>' +
      '<span class="amh-sp amh-sp2" aria-hidden="true">✦</span>' +
      '<span class="amh-sp amh-sp3" aria-hidden="true">✦</span>' +
      '<span class="amh-star" aria-hidden="true">★</span>' +
      "<span>" + esc(t("bal", { n: me.balance })) + "</span></a>";
    st.writing = false;
  }

  function mountCard(el) {
    if (!el || el.dataset.amhReady === "1") return;
    el.dataset.amhReady = "1";
    LANG = (el.dataset.lang || "en").toLowerCase().slice(0, 2);
    addCss();
    watchSelf(el);
    if (!ME) renderLoading(el);
    refresh(el, true);
  }

  /**
   * The theme redraws the cart by diffing fresh server HTML against the live DOM. Our node survives
   * that — it is a custom tag, so it can never be mutated into cart markup — but its CHILDREN come
   * from the server, which means empty, and so does its attribute set, which erases the marker that
   * says we already mounted it. The card is then present, 400px wide and 0 high.
   *
   * The theme's cart events cover most redraws but not all of them, and a redraw we do not hear
   * about leaves an empty card on screen — exactly what happened on artmie.ba. So each card also
   * watches ITSELF: one observer, on one element, childList only. This is not the page-wide
   * observer the research warned about, which would feed the three the theme already runs under the
   * drawer. Our own writes are fenced off with `writing`, and the refill comes from memory, so it
   * can neither loop nor call the Hub again.
   */
  function watchSelf(el) {
    if (el.__amhWatch) return;
    try {
      el.__amhWatch = new MutationObserver(function () {
        var st = stateOf(el);
        if (st.writing || st.busy) return;
        if (el.children.length) return;                              // still drawn: nothing to do
        if (el.dataset.amhReady !== "1") el.dataset.amhReady = "1";  // the redraw erased the marker
        if (ME) { try { el.dataset.mode === "badge" ? renderBadge(el) : render(el); } catch (e) { /* never throw into the storefront */ } }
        else refresh(el, true);
      });
      el.__amhWatch.observe(el, { childList: true });
    } catch (e) { /* old browser: the events below still cover the common redraws */ }
  }

  /**
   * The card is rendered by the theme, so after every cart change morph replaces its children with
   * the server's (empty) ones. Re-fill on the theme's own events; no MutationObserver.
   */
  var pending = false;
  function remount() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      try {
        each(document.querySelectorAll("amh-loyalty-card"), function (el) {
          if (el.dataset.amhReady === "1" && el.children.length) {
            // Still ours and still drawn — but this event means the BASKET changed, and the card
            // now says things that depend on it: what the order is worth in points, and which of
            // our codes are on it. Returning here left "you will collect about 57 points" sitting
            // under a 141 KM basket. Re-read instead. `me` comes from its 20-second cache, so this
            // costs one /cart.js call and no round trip to the Hub.
            if (!stateOf(el).busy) refresh(el, false);
            return;
          }
          el.dataset.amhReady = "";
          mountCard(el);
        });
      } catch (e) { /* never throw into the storefront */ }
    });
  }

  // When the redraw replaces the node outright rather than updating it in place, the new one is a
  // fresh element and connectedCallback is the only thing that fires. Registering the tag covers it.
  try {
    if (window.customElements && !customElements.get("amh-loyalty-card")) {
      customElements.define("amh-loyalty-card", class extends HTMLElement {
        connectedCallback() { var el = this; setTimeout(function () { try { mountCard(el); } catch (e) { /* never throw */ } }, 0); }
      });
    }
  } catch (e) { /* never break the storefront over a registration clash */ }

  function start() {
    try {
      each(document.querySelectorAll("amh-loyalty-card"), mountCard);
      watchDrawer();
      each(document.querySelectorAll(".amh-page"), function (p) { p.classList.add("amh-page"); });
    } catch (e) { /* never throw into the storefront */ }
  }

  ["cart:update", "cart:refresh", "discount:update"].forEach(function (name) {
    document.addEventListener(name, function () { remount(); });
  });
  // Opening the drawer is itself a redraw, and dialog:open does NOT bubble — bind it on the drawer.
  function watchDrawer() {
    try {
      each(document.querySelectorAll("cart-drawer-component"), function (d) {
        if (d.__amhOpen) return;
        d.__amhOpen = true;
        d.addEventListener("dialog:open", function () { remount(); });
      });
    } catch (e) { /* never throw into the storefront */ }
  }
  // The drawer is rendered with the page, but the theme may swap the header section in later.
  [0, 500, 1500].forEach(function (ms) { setTimeout(start, ms); });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);

  // exposed only so the points-page template can force a redraw if it needs to
  window.__amhLoyaltyRefresh = function () { each(document.querySelectorAll("amh-loyalty-card"), function (el) { refresh(el, true); }); };
  window.__amhGiveBack = giveBack;
})();
